// The strategic plan: an institution's consolidated projects (domain/consolidate.ts) as the planning workbook lays
// them out (VPC_strateginiai_projektai_2026-2030.xlsx), and what the site keeps beside them.
//   workbook   two sheets. "Strateginis planas", a row per project: B the VULSK direction, goal and strategic
//              project it serves, C the project and its goal ("Tikslas:"), D who is responsible, E its subprojects
//              ("1.1. …"), F where it stands, G–K a target a year (2026–2030), L the resources to weigh. Below them,
//              blocks of labelled texts (the planning assumptions, the sources) and a closing note.
//              "Uždaviniai ir KPI", a row per subproject: its code, project, activities, who is responsible, its
//              main KPI with its definition, baseline, sign, unit, a target a year, the main deadline, and what it
//              depends on ("1.1–1.2. …"). Columns are found by their headers, not their places.
//   stored     the changes' section "plan" (domain/strategy.ts), beside the consolidation, each under its own key:
//                <inst>          the workbook's own texts: titles, notes, blocks, closing note (Lithuanian)
//                <inst>|<code>   a project's (P1…): its VULSK alignment, state, resources, the workbook projects
//                                merged into it (parts)
//                <inst>|<sub>    a subproject's: its KPI, deadline, the subprojects it needs, its conditions
//              Two people reviewing different KPIs never overwrite each other. Titles, activities, milestones and
//              leads stay in the consolidation: the board, the Gantt and the presentation read them there.
//   import     readPlanBook() reads a workbook. One laid out as the site's projects (an export, edited) updates them
//              in place (updateBook); another (a proposal with its own projects) is merged into them (mergeBook):
//              each of its projects into a consolidated project, its subprojects in place of the ones they
//              replace. mergeProposal() guesses where from the words they share; people change it before saving.
//   export     bookOf(): the same two sheets for the whole consolidation, every project and subproject. Codes are
//              places ("3.2": the third project's second subproject), so a subproject named in a text is kept as
//              {#id} and written with its code of the day.
// Texts come in English and Lithuanian as everywhere in the consolidation (`kpi` and `kpiLt`…); the workbook is
// Lithuanian, so what it brings is Lithuanian until translated.
import {
  CONSOLIDATION_YEARS,
  elementsOf,
  institutionsOf,
  monthIn,
  ownText,
  savedConsolidation,
  subPeriod,
  type ConsolidatedProject,
  type Consolidation,
  type ConsolidationLang,
  type Milestone,
  type PlanElement,
  type SubProject,
  type Activity,
} from './consolidate.ts';
import type { StrategyChange, StrategyState } from './strategy.ts';

type Obj = Record<string, unknown>;
const isObj = (v: unknown): v is Obj => !!v && typeof v === 'object' && !Array.isArray(v);
const s = (v: unknown): string => (v == null ? '' : String(v));
const list = (v: unknown): Obj[] => (Array.isArray(v) ? v.filter(isObj) : []);

// ── what is stored ──────────────────────────────────────────────────────────

export const PLAN_SIGNS = ['≥', '≤', '='] as const;
export type PlanSign = (typeof PLAN_SIGNS)[number];

/** A labelled text under a heading: a planning assumption, a source. */
export interface PlanItem {
  label: string;
  text: string;
}
export interface PlanBlock {
  heading: string;
  items: PlanItem[];
}

/** The workbook's own texts, Lithuanian as it has them. Key `<inst>`. */
export interface PlanMeta {
  title?: string;
  notes?: string[];
  kpiTitle?: string;
  kpiNotes?: string[];
  blocks?: PlanBlock[];
  footer?: string[];
  /** The file last imported, when and by whom. */
  file?: string;
  at?: string;
  by?: string;
}

/** A workbook project merged into a consolidated project: its short name (sheet 2's "Projektas"), title and goal. Lithuanian. */
export interface PlanPart {
  short: string;
  title: string;
  goal: string;
  /** Their English, when typed or translated on the site (the workbook has none). */
  titleEn?: string;
  goalEn?: string;
}

/** A project's texts of the workbook. Key `<inst>|<code>`. */
export interface ProjectPlan {
  /** Its short name, for sheet 2's "Projektas" (Lithuanian). */
  short?: string;
  vulsk?: string;
  vulskLt?: string;
  state?: string;
  stateLt?: string;
  resources?: string;
  resourcesLt?: string;
  parts?: PlanPart[];
  mt?: Partial<Record<ProjectPlanText, ConsolidationLang>>;
  old?: Partial<Record<ProjectPlanText, ConsolidationLang>>;
}
export type ProjectPlanText = 'vulsk' | 'state' | 'resources';

/** A subproject's KPI and plan. Key `<inst>|<subproject id>`. */
export interface SubPlan {
  /** The workbook project it came in with (its short name), and its code there ("1.3"). */
  part?: string;
  from?: string;
  /**
   * What the subproject is to achieve, shown under its name: the workbook's "Uždavinys / veiksmai" (sheet 2, C); with none,
   * one is proposed from its KPI (suggestedGoal) until someone writes or keeps one.
   */
  goal?: string;
  goalLt?: string;
  kpi?: string;
  kpiLt?: string;
  def?: string;
  defLt?: string;
  /** The baseline: a fraction for a % (0.16 is 16 %), as the workbook keeps it. null: not known yet. */
  base?: number | null;
  baseNote?: string;
  baseNoteLt?: string;
  sign?: PlanSign;
  unit?: string;
  unitLt?: string;
  /** A target a year, 2026 first; null: none that year. Fractions for a %. */
  t?: (number | null)[];
  /** The main deadline: the first rollout or the final target (YYYY-MM-DD). */
  due?: string;
  /** The subprojects it needs (their ids). */
  needs?: string[];
  /**
   * Its dependencies' and conditions' text as written ("1.1–1.2. Išorės gavėjų…"); a subproject named in it is {#id}.
   * The codes it starts with are `needs` (depsText): when they no longer are, the export writes `needs` instead.
   */
  cond?: string;
  condLt?: string;
  /** Agreed in the review: by whom and when. Without it the KPI is proposed. */
  ok?: { by: string; at: string };
  mt?: Partial<Record<SubPlanText, ConsolidationLang>>;
  old?: Partial<Record<SubPlanText, ConsolidationLang>>;
}
export type SubPlanText = 'goal' | 'kpi' | 'def' | 'baseNote' | 'unit' | 'cond';

/** The keys a plan is kept under, in the changes' section "plan". */
export const planKey = (inst: string, id?: string): string => (id ? `${inst}|${id}` : inst);
/** A project's code (P1…), as against a subproject's id. */
const PROJECT_CODE = /^P\d+$/;

const at = (plan: Record<string, unknown>, key: string): Obj => (isObj(plan[key]) ? plan[key] : {});
export const planMeta = (plan: Record<string, unknown>, inst: string): PlanMeta => at(plan, planKey(inst)) as PlanMeta;
export const projectPlan = (plan: Record<string, unknown>, inst: string, code: string): ProjectPlan => at(plan, planKey(inst, code)) as ProjectPlan;
export const subPlan = (plan: Record<string, unknown>, inst: string, id: string): SubPlan => at(plan, planKey(inst, id)) as SubPlan;

/** A KPI's values: the review agrees to these. One changed makes the KPI a proposal again. */
export const PLAN_VALUES = ['kpi', 'kpiLt', 'base', 'sign', 'unit', 'unitLt', 't', 'due'] as const;
/** Whether a value of the KPI differs (none and null alike). */
const valuesChanged = (a: SubPlan, b: SubPlan): boolean => PLAN_VALUES.some((k) => JSON.stringify(a[k] ?? null) !== JSON.stringify(b[k] ?? null));
/** A subproject's plan with `fields` set: a changed value takes the agreement (`ok`) away, as everywhere a KPI is edited. */
export function editPlan(sp: SubPlan, fields: Partial<SubPlan>): SubPlan {
  const next: SubPlan = { ...sp, ...fields };
  if (valuesChanged(sp, next)) delete next.ok;
  return next;
}

/** The most characters of one text in a plan. */
const TEXT_MAX = 8000;

/** The texts of each kind of plan entry: strings when there, as the readers take them. */
const ENTRY_TEXTS: Record<'meta' | 'project' | 'sub', readonly string[]> = {
  meta: ['title', 'kpiTitle', 'file', 'at', 'by'],
  project: ['short', 'vulsk', 'vulskLt', 'state', 'stateLt', 'resources', 'resourcesLt'],
  sub: ['part', 'from', 'goal', 'goalLt', 'kpi', 'kpiLt', 'def', 'defLt', 'baseNote', 'baseNoteLt', 'unit', 'unitLt', 'cond', 'condLt'],
};
const isText = (v: unknown) => v === undefined || typeof v === 'string';
/** None, or a list whose every item is `ok`. */
const listOf = (v: unknown, ok: (x: unknown) => boolean) => v === undefined || (Array.isArray(v) && v.every(ok));
/** A text's marks (`mt`, `old`): the language of each field's, en or lt. */
const isMarks = (v: unknown) => v === undefined || (isObj(v) && Object.values(v).every((x) => x === undefined || x === 'en' || x === 'lt'));

/** Why a plan entry cannot be stored, or ''. */
export function planEntryProblem(key: string, value: unknown): string {
  if (value === null) return '';
  if (!isObj(value)) return 'A plan entry is an object.';
  const [inst, id, more] = key.split('|');
  if (!inst?.trim() || more !== undefined || id === '') return 'Unknown plan entry.';
  const texts = Object.entries(value).filter(([, v]) => typeof v === 'string') as [string, string][];
  const long = texts.find(([, v]) => v.length > TEXT_MAX);
  if (long) return `“${long[0]}” is longer than ${TEXT_MAX.toLocaleString('en-US')} characters.`;
  // The types the readers take: one entry of another shape would break the page for everyone.
  const kind = id === undefined ? 'meta' : PROJECT_CODE.test(id) ? 'project' : 'sub';
  const notText = ENTRY_TEXTS[kind].find((k) => !isText(value[k]));
  if (notText) return `“${notText}” is a text.`;
  if (kind === 'meta') {
    const lines = (['notes', 'kpiNotes', 'footer'] as const).find((k) => !listOf(value[k], (x) => typeof x === 'string'));
    if (lines) return `“${lines}” is a list of texts.`;
    const item = (x: unknown) => isObj(x) && typeof x.label === 'string' && typeof x.text === 'string';
    return listOf(value.blocks, (b) => isObj(b) && typeof b.heading === 'string' && Array.isArray(b.items) && b.items.every(item)) ? '' : 'Blocks are headings, each with labelled texts.';
  }
  if (!isMarks(value.mt) || !isMarks(value.old)) return 'A text’s marks are en or lt.';
  if (kind === 'project') {
    const part = (x: unknown) => isObj(x) && typeof x.short === 'string' && typeof x.title === 'string' && typeof x.goal === 'string' && isText(x.titleEn) && isText(x.goalEn);
    return listOf(value.parts, part) ? '' : 'Parts are workbook projects: a short name, a title and a goal.';
  }
  const ok = value.ok;
  if (ok !== undefined && !(isObj(ok) && typeof ok.by === 'string' && typeof ok.at === 'string')) return 'An agreement is who agreed and when.';
  const v = value as SubPlan;
  if (v.sign !== undefined && !(PLAN_SIGNS as readonly string[]).includes(v.sign)) return `A KPI's sign is ${PLAN_SIGNS.join(' ')}.`;
  const num = (n: unknown) => n === null || (typeof n === 'number' && Number.isFinite(n));
  if (v.base !== undefined && !num(v.base)) return 'A baseline is a number, or null when not known.';
  if (v.t !== undefined && (!Array.isArray(v.t) || v.t.length > CONSOLIDATION_YEARS.length || !v.t.every(num))) return `Targets are up to ${CONSOLIDATION_YEARS.length} numbers, a year each (null: none).`;
  if (v.due !== undefined && !/^20\d\d-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/.test(String(v.due))) return 'A deadline is a date: YYYY-MM-DD.';
  if (v.needs !== undefined && (!Array.isArray(v.needs) || !v.needs.every((x) => typeof x === 'string'))) return 'What a subproject needs is a list of subprojects.';
  return '';
}

// ── reading a workbook ──────────────────────────────────────────────────────

/** A cell as a workbook reader gives it. */
export type Cell = string | number | boolean | Date | null | undefined;
export interface RawSheet {
  sheet: string;
  data: Cell[][];
}

/** A project row of the workbook. */
export interface BookProject {
  nr: number;
  vulsk: string;
  title: string;
  goal: string;
  /** Other projects' titles and goals in its C cell: the parts of a merged project, as an export writes them. */
  parts: { title: string; goal: string }[];
  lead: string;
  subs: { code: string; title: string }[];
  state: string;
  years: string[];
  resources: string;
}

/** A subproject row of the workbook. */
export interface BookRow {
  code: string;
  project: string;
  text: string;
  lead: string;
  kpi: string;
  def: string;
  base: number | null;
  baseNote: string;
  sign: string;
  unit: string;
  t: (number | null)[];
  due: string;
  deps: string;
}

/** A planning workbook as read: both sheets. */
export interface PlanBook {
  names: [string, string];
  title: string;
  notes: string[];
  projects: BookProject[];
  blocks: PlanBlock[];
  footer: string[];
  kpiTitle: string;
  kpiNotes: string[];
  rows: BookRow[];
  /** The columns whose headers were not found: their cells say nothing, so an import leaves what they hold. Only when some are. */
  absent?: { projects: PlanOptional[]; rows: KpiOptional[] };
}
/** The columns a workbook may lack: sheet 1's, sheet 2's. */
type PlanOptional = 'lead' | 'state' | 'resources';
type KpiOptional = 'project' | 'text' | 'lead' | 'base' | 'baseNote' | 'due' | 'deps';
const PLAN_OPTIONAL: readonly PlanOptional[] = ['lead', 'state', 'resources'];
const KPI_OPTIONAL: readonly KpiOptional[] = ['project', 'text', 'lead', 'base', 'baseNote', 'due', 'deps'];

const norm = (v: Cell): string => s(v instanceof Date ? '' : v).toLowerCase().replace(/\s+/g, ' ').trim();
const text = (v: Cell): string => (v instanceof Date ? isoDay(v) : typeof v === 'string' ? v.replace(/\r\n?/g, '\n').trim() : v == null ? '' : String(v));
/** A date cell's day. Readers give a date at midnight UTC. */
const isoDay = (d: Date): string => `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
/** A text as an export's cell holds it (web planXlsx.ts xmlChars): a Word line break or a page break (U+000B, U+000C) a space; what XML 1.0 cannot hold gone. */
const cellChars = (t: string): string => t.replace(/[\v\f]/g, ' ').replace(/[^\t\n\r\x20-퟿-�\u{10000}-\u{10FFFF}]/gu, '');
/** A text written to a cell and read back: as the cell holds it, then as the reader takes it (trimmed, CR LF a line break). */
const readText = (t: string): string => text(cellChars(t));

/** The deadline cell: a date cell's day, a day number's (a date cell left with no format), or a text starting with one. */
function dueOf(v: Cell): string {
  if (v instanceof Date) return isoDay(v);
  if (typeof v === 'number' && Number.isFinite(v)) {
    // Excel counts days from 1899-12-30; a number that is no day of these years (2027 typed as a year) is none.
    const d = new Date(Date.UTC(1899, 11, 30) + Math.floor(v) * 86_400_000);
    return d.getUTCFullYear() >= 2000 && d.getUTCFullYear() < 2100 ? isoDay(d) : '';
  }
  return /^(20\d\d-\d\d-\d\d)/.exec(text(v))?.[1] ?? '';
}

/** A number cell, or one typed as text ("16 %", "0,995"); else null. */
function numberOf(v: Cell): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v !== 'string' || !v.trim()) return null;
  const m = /^\s*([-+]?\d+(?:[.,]\d+)?)\s*(%?)\s*$/.exec(v);
  if (!m) return null;
  const n = Number(m[1]!.replace(',', '.'));
  return m[2] ? n / 100 : n;
}

/** The headers each sheet is found by, and its columns: the first header that matches. */
const PLAN_COLS = {
  nr: /^nr\.?$/,
  vulsk: /vulsk/,
  project: /projektas/,
  lead: /atsakingas/,
  subs: /uždavin|veiksm/,
  state: /esama|būsena/,
  resources: /ištekli/,
} as const;
const KPI_COLS = {
  code: /^kodas/,
  project: /^projektas/,
  text: /uždavin|veiksm/,
  lead: /atsakingas/,
  kpi: /rodiklis/,
  def: /apibrėžt/,
  base: /^bazinė/,
  baseNote: /laikotarp|būkl/,
  sign: /ženkl/,
  unit: /vienet/,
  due: /terminas/,
  deps: /priklausom/,
} as const;
const yearCol = (y: number) => new RegExp(`^${y}\\s*m\\.?\\s*tiksl`);

/** The header row (the first with all `need`) and each column's place. */
function headers<K extends string>(data: Cell[][], cols: Record<K, RegExp>, need: NoInfer<K>[]): { row: number; at: Record<K, number>; years: number[] } | null {
  for (let r = 0; r < Math.min(data.length, 30); r++) {
    const cells = (data[r] ?? []).map(norm);
    const at = {} as Record<K, number>;
    for (const k of Object.keys(cols) as K[]) {
      // A column is taken once: "Projektas" is not also "VPC projektas" once that is found.
      const used = new Set(Object.values(at) as number[]);
      at[k] = cells.findIndex((c, i) => !used.has(i) && cols[k].test(c));
    }
    const years = CONSOLIDATION_YEARS.map((y) => cells.findIndex((c) => yearCol(y).test(c)));
    if (need.every((k) => at[k] >= 0) && years.every((i) => i >= 0)) return { row: r, at, years };
  }
  return null;
}

/** Paragraphs: parts of a text between blank lines. */
const paragraphs = (t: string): string[] => t.split(/\n[ \t]*\n/).map((x) => x.trim()).filter(Boolean);

/** A goal's line in a C cell. */
const GOAL_LINE = /^Tikslas:\s*/i;

/** A paragraph of a title and its goal: the lines before its first "Tikslas:" line (not the first line), then the rest. */
function titleAndGoal(p: string): { title: string; goal: string } | null {
  const lines = p.split('\n');
  const k = lines.findIndex((l, j) => j > 0 && GOAL_LINE.test(l.trim()));
  return k > 0 ? { title: lines.slice(0, k).join('\n').trim(), goal: lines.slice(k).join('\n').trim().replace(GOAL_LINE, '') } : null;
}

/** A project's C cell: its title, its goal ("Tikslas: …"), then other titles with their goals (the parts of a merged one). */
export function splitTitleGoal(t: string): { title: string; goal: string; parts: { title: string; goal: string }[] } {
  const ps = paragraphs(t);
  const out = { title: '', goal: '', parts: [] as { title: string; goal: string }[] };
  ps.forEach((p, i) => {
    const goal = /^Tikslas:\s*([\s\S]*)$/i.exec(p);
    const tg = titleAndGoal(p);
    const part = out.parts[out.parts.length - 1];
    // The title; its goal on the next line ("Duomenys\nTikslas: …") is its goal.
    if (i === 0 && !goal) Object.assign(out, tg ?? { title: p });
    else if (goal && !out.goal && !out.parts.length) out.goal = goal[1]!.trim();
    // A part: its title, then its goal from its "Tikslas:" line on, every line of it.
    else if (tg) out.parts.push(tg);
    // A paragraph after a part goes on with its goal: an export writes the project's own goal before the parts.
    else if (part) part.goal = part.goal ? `${part.goal}\n\n${p}` : p;
    else if (!out.title) out.title = p;
    else out.goal = out.goal ? `${out.goal}\n\n${p}` : p;
  });
  return out;
}

/** A project's C cell from its title, goal and parts (splitTitleGoal reads it back). */
export function joinTitleGoal(title: string, goal: string, parts: { title: string; goal: string }[] = []): string {
  return [title, goal ? `Tikslas: ${goal}` : '', ...parts.map((p) => `${p.title}\nTikslas: ${p.goal}`)].filter(Boolean).join('\n\n');
}

/** An E cell's subprojects: "1.1. Inventorizuoti …" paragraphs; a line starting with a code and its dot starts one too. */
export function subsOf(t: string): { code: string; title: string }[] {
  return paragraphs(t)
    .flatMap((p) => p.split(/\n(?=[ \t]*\d{1,2}\.\d{1,2}\.\s)/))
    .flatMap((p) => {
      const m = /^(\d+\.\d+)\.?\s+([\s\S]+)$/.exec(p.trim());
      return m ? [{ code: m[1]!, title: m[2]!.trim() }] : [];
    });
}

/** An E cell from its subprojects, as an export writes it (subsOf reads it back). */
export const joinSubs = (subs: { code: string; title: string }[]): string => subs.map((x) => `${x.code}. ${x.title}`).join('\n\n');

/** A code cell: "1.1" kept as typed; a number read as one (1.1) back to its text. */
const codeOf = (v: Cell): string => {
  const t = typeof v === 'number' ? String(v) : text(v);
  return /^\d+\.\d+$/.test(t) ? t : '';
};

/** A row's texts, left to right. A merged cell is read into each cell it covers: once here. */
const texts = (r: Cell[]): string[] => r.map(text).filter((t, i, all) => t && t !== all[i - 1]);

/** The labelled blocks below the projects (heading, then label / text rows) and the closing note lines. */
function blocksOf(rows: Cell[][]): { blocks: PlanBlock[]; footer: string[] } {
  const lines = rows
    .map(texts)
    .filter((r) => r.length)
    .map((r) => ({ label: r[0]!, text: r.slice(1).join('\n') }));
  const blocks: PlanBlock[] = [];
  const footer: string[] = [];
  lines.forEach((l, i) => {
    if (l.text) {
      if (!blocks.length) blocks.push({ heading: '', items: [] });
      blocks[blocks.length - 1]!.items.push(l);
    } else if (lines[i + 1]?.text) blocks.push({ heading: l.label, items: [] });
    else footer.push(l.label);
  });
  return { blocks, footer };
}

/** The texts above a sheet's header row: its title, then its notes. */
function topOf(data: Cell[][], header: number): { title: string; notes: string[] } {
  const t = data.slice(0, header).flatMap(texts);
  return { title: t[0] ?? '', notes: t.slice(1) };
}

/** A planning workbook's two sheets, by their headers; null when a workbook is not one. */
export function readPlanBook(sheets: RawSheet[]): PlanBook | null {
  let planSheet: { sh: RawSheet; h: NonNullable<ReturnType<typeof headers<keyof typeof PLAN_COLS>>> } | null = null;
  let kpiSheet: { sh: RawSheet; h: NonNullable<ReturnType<typeof headers<keyof typeof KPI_COLS>>> } | null = null;
  for (const sh of sheets) {
    const k = headers(sh.data, KPI_COLS, ['code', 'kpi', 'def', 'sign', 'unit']);
    if (k && !kpiSheet) {
      kpiSheet = { sh, h: k };
      continue;
    }
    const p = headers(sh.data, PLAN_COLS, ['nr', 'vulsk', 'project', 'subs']);
    if (p && !planSheet) planSheet = { sh, h: p };
  }
  if (!planSheet || !kpiSheet) return null;

  const { sh: ps, h: ph } = planSheet;
  const cell = (r: Cell[], i: number) => (i >= 0 ? text(r[i]) : '');
  const projects: BookProject[] = [];
  let r = ph.row + 1;
  for (; r < ps.data.length; r++) {
    const row = ps.data[r] ?? [];
    const nr = numberOf(row[ph.at.nr]);
    if (nr === null) {
      if (row.some((c) => text(c))) break;
      continue;
    }
    const tg = splitTitleGoal(cell(row, ph.at.project));
    projects.push({
      nr,
      vulsk: cell(row, ph.at.vulsk),
      title: tg.title,
      goal: tg.goal,
      parts: tg.parts,
      lead: cell(row, ph.at.lead),
      subs: subsOf(cell(row, ph.at.subs)),
      state: cell(row, ph.at.state),
      years: ph.years.map((i) => cell(row, i)),
      resources: cell(row, ph.at.resources),
    });
  }
  const { blocks, footer } = blocksOf(ps.data.slice(r));

  const { sh: ks, h: kh } = kpiSheet;
  const rows: BookRow[] = [];
  for (const row of ks.data.slice(kh.row + 1)) {
    const code = codeOf(row[kh.at.code]);
    if (!code) continue;
    rows.push({
      code,
      project: cell(row, kh.at.project),
      text: cell(row, kh.at.text),
      lead: cell(row, kh.at.lead),
      kpi: cell(row, kh.at.kpi),
      def: cell(row, kh.at.def),
      base: kh.at.base >= 0 ? numberOf(row[kh.at.base]) : null,
      baseNote: cell(row, kh.at.baseNote),
      sign: signOf(cell(row, kh.at.sign)),
      unit: cell(row, kh.at.unit),
      t: kh.years.map((i) => numberOf(row[i])),
      due: kh.at.due >= 0 ? dueOf(row[kh.at.due]) : '',
      deps: cell(row, kh.at.deps),
    });
  }
  if (!projects.length || !rows.length) return null;
  const absent = { projects: PLAN_OPTIONAL.filter((k) => ph.at[k] < 0), rows: KPI_OPTIONAL.filter((k) => kh.at[k] < 0) };
  return {
    names: [ps.sheet, ks.sheet],
    ...topOf(ps.data, ph.row),
    projects,
    blocks,
    footer,
    kpiTitle: topOf(ks.data, kh.row).title,
    kpiNotes: topOf(ks.data, kh.row).notes,
    rows,
    ...(absent.projects.length || absent.rows.length ? { absent } : {}),
  };
}

/** A sign as typed: ≥, ≤, = (>=, <= too); '' none. */
function signOf(t: string): string {
  const v = t.replace(/\s/g, '');
  return v === '>=' || v === '≥' ? '≥' : v === '<=' || v === '≤' ? '≤' : v === '=' ? '=' : '';
}

// ── texts ───────────────────────────────────────────────────────────────────

/** Words a sentence does not end at: "2027 m. I ketv.", "pvz., …", "t. y.". */
const ABBR = new Set(['m', 'd', 'mėn', 'ketv', 'pvz', 't', 'y', 'kt', 'val', 'min', 'proc', 'nr', 'str', 'sk', 'p', 'pan', 'tt', 'vnt', 'e', 'g', 'i', 'etc']);

/** A text's sentences: its activities. Joined by a space, they give the text back. */
export function sentences(t: string): string[] {
  const out: string[] = [];
  let start = 0;
  const re = /[.!?](\s+)(?=[A-ZĄČĘĖĮŠŲŪŽ„"(])/g;
  for (let m = re.exec(t); m; m = re.exec(t)) {
    const word = /([\p{L}\d]+)$/u.exec(t.slice(start, m.index))?.[1] ?? '';
    if (ABBR.has(word.toLowerCase()) || /^\d+$/.test(word)) continue;
    out.push(t.slice(start, m.index + 1).trim());
    start = m.index + 1 + m[1]!.length;
  }
  const rest = t.slice(start).trim();
  if (rest) out.push(rest);
  return out;
}

// "?": a subproject removed since (withCodes), still one of the codes the text starts with.
const CODE = String.raw`(?:\d+\.\d+|\?)`;
const RANGE = String.raw`${CODE}(?:\s*[–-]\s*${CODE})?`;
const LEADING = new RegExp(String.raw`^\s*(${RANGE}(?:\s*(?:,|ir|and)\s*${RANGE})*)\s*\.(?:\s+|$)`);

/** The codes a dependencies text starts with ("1.1–1.2. …", "2.1 ir 1.2. …", "4.1, 1.3–1.4. …"), ranges spelled out, and the rest. */
export function leadingCodes(t: string): { codes: string[]; rest: string } {
  const m = LEADING.exec(t);
  if (!m) return { codes: [], rest: t.trim() };
  const codes: string[] = [];
  for (const part of m[1]!.split(/\s*(?:,|\bir\b|\band\b)\s*/)) {
    const [a, b] = part.split(/\s*[–-]\s*/);
    if (!a) continue;
    const [ma, na] = a.split('.').map(Number) as [number, number];
    const [mb, nb] = (b ?? a).split('.').map(Number) as [number, number];
    if (ma === mb && nb >= na && nb - na < 50) for (let n = na; n <= nb; n++) codes.push(`${ma}.${n}`);
    else codes.push(a, ...(b ? [b] : []));
  }
  return { codes: [...new Set(codes)], rest: t.slice(m[0].length).trim() };
}

/** Codes as a dependencies text starts them: runs as ranges ("3.1–3.3"), the last joined by "ir". */
export function codesText(codes: string[]): string {
  const groups: string[][] = [];
  for (const c of codes) {
    const g = groups[groups.length - 1];
    const prev = g?.[g.length - 1];
    const [pm, pn] = (prev ?? '').split('.').map(Number);
    const [m, n] = c.split('.').map(Number);
    if (g && pm === m && pn !== undefined && n === pn + 1) g.push(c);
    else groups.push([c]);
  }
  const parts = groups.map((g) => (g.length > 1 ? `${g[0]}–${g[g.length - 1]}` : g[0]!));
  return parts.length > 1 ? `${parts.slice(0, -1).join(', ')} ir ${parts[parts.length - 1]}` : (parts[0] ?? '');
}

const CODE_IN_TEXT = /(?<![\d.,])(\d{1,2}\.\d{1,2})(?![\d%]|[.,]\d)/g;

/** A text with each subproject code it names (`ids`: code → id) as {#id}: a code moves when the subproject does. */
export const tokenized = (t: string, ids: ReadonlyMap<string, string>): string => t.replace(CODE_IN_TEXT, (c) => (ids.has(c) ? `{#${ids.get(c)}}` : c));
/** The codes the subprojects in a text have now (`codes`: id → code); a removed one is "?". */
export const withCodes = (t: string, codes: ReadonlyMap<string, string>): string => t.replace(/\{#([^}]+)\}/g, (_, id: string) => codes.get(id) ?? '?');

/** The codes in a consolidation, by subproject: "2.3" the second project's third subproject. */
export function subCodes(c: Consolidation): Map<string, string> {
  return new Map(c.projects.flatMap((p, pi) => p.subs.map((x, si) => [x.id, `${pi + 1}.${si + 1}`] as const)));
}

/**
 * A subproject's dependencies as the workbook's cell holds them: its conditions' text with today's codes, starting with
 * what it needs. Written as typed while its first codes are what it needs; else those codes are written anew.
 */
export function depsText(cond: string, needs: string[], codes: ReadonlyMap<string, string>): string {
  const text = withCodes(cond, codes);
  const lead = leadingCodes(text);
  const want = [...new Set(needs.map((id) => codes.get(id)).filter((c): c is string => !!c))];
  if (lead.codes.length === want.length && want.every((c) => lead.codes.includes(c))) return text;
  return [want.length ? `${codesText(want)}.` : '', lead.rest].filter(Boolean).join(' ');
}

/** `needs` read again from a dependencies text typed: the codes it starts with, as the subprojects they are. */
export function needsOf(text: string, ids: ReadonlyMap<string, string>): string[] {
  return leadingCodes(text)
    .codes.map((c) => ids.get(c))
    .filter((x): x is string => !!x);
}

/** A number as the plan shows it: a % as percent ("30 %", "99,5 %"), else as it is ("2"). Lithuanian decimal comma. */
export function planNumber(v: number | null | undefined, unit = ''): string {
  if (v == null) return '';
  const pct = unit.trim() === '%';
  const n = Math.round((pct ? v * 100 : v) * 1000) / 1000;
  return `${String(n).replace('.', ',')}${pct ? ' %' : ''}`;
}

/** A number typed for a KPI ("30", "30 %", "99,5") as kept: a fraction for a %. NaN when it is none. */
export function typedNumber(t: string, unit = ''): number | null {
  const v = t.replace(/\s|%/g, '').replace(',', '.');
  if (!v) return null;
  if (!/^[-+]?\d+(\.\d+)?$/.test(v)) return NaN;
  return unit.trim() === '%' ? Number(v) / 100 : Number(v);
}

// ── checks ──────────────────────────────────────────────────────────────────

export type PlanIssue = 'noKpi' | 'noTargets' | 'noBase' | 'zeroBase' | 'direction' | 'over100' | 'noLead' | 'noDue' | 'badNeed';

/** A KPI that measures a reduction from its baseline: a percentage of 0 cannot be worked out. */
const REDUCTION = /sumažėjim|sumažinim|reduction|decrease/i;

/** What a subproject's plan still lacks or gets wrong, for the review. `known`: the subprojects there are. */
export function planIssues(sp: SubPlan, lead: string, known: ReadonlySet<string>): PlanIssue[] {
  const out: PlanIssue[] = [];
  const kpi = sp.kpiLt || sp.kpi;
  const t = (sp.t ?? []).filter((x): x is number => typeof x === 'number');
  if (!kpi) out.push('noKpi');
  else if (!t.length) out.push('noTargets');
  if (kpi && sp.base == null) out.push('noBase');
  if (sp.base === 0 && REDUCTION.test(`${sp.kpiLt ?? ''} ${sp.kpi ?? ''}`)) out.push('zeroBase');
  if (t.some((x, i) => i > 0 && (sp.sign === '≥' ? x < t[i - 1]! : sp.sign === '≤' ? x > t[i - 1]! : false))) out.push('direction');
  if ((sp.unitLt || sp.unit || '').trim() === '%' && [...t, sp.base ?? 0].some((x) => x > 1 || x < 0)) out.push('over100');
  if (!lead.trim()) out.push('noLead');
  if (kpi && !sp.due) out.push('noDue');
  if ((sp.needs ?? []).some((id) => !known.has(id))) out.push('badNeed');
  return out;
}

/** The issues that are gaps to fill rather than mistakes. */
export const PLAN_GAPS: readonly PlanIssue[] = ['noKpi', 'noTargets', 'noBase', 'noLead', 'noDue'];

// ── matching a proposal to the consolidation ────────────────────────────────

const STOP = new Set(['ir', 'ar', 'su', 'be', 'is', 'per', 'del', 'nuo', 'iki', 'pagal', 'kaip', 'kad', 'tai', 'jo', 'ju', 'bei', 'the', 'and', 'of', 'in', 'to', 'for', 'on', 'a', 'an', 'with', 'by', 'vpc', 'kt']);

/** A text's word stems: lower case, no accents, the first 6 letters ("struktūruotų" and "struktūrizuoti" meet). */
function stems(t: string): Set<string> {
  const words = t.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').match(/[a-z0-9]+/g) ?? [];
  return new Set(words.filter((w) => w.length >= 2 && !STOP.has(w) && !/^\d+$/.test(w)).map((w) => w.slice(0, 6)));
}

/** How rare each stem is among `docs`: a word every element has says nothing. */
function rarity(docs: Set<string>[]): (stem: string) => number {
  const df = new Map<string, number>();
  for (const d of docs) for (const w of d) df.set(w, (df.get(w) ?? 0) + 1);
  return (w) => Math.log((docs.length + 1) / ((df.get(w) ?? 0) + 0.5));
}

/** Where a proposal's projects go: each into a consolidated project (its code; '' a new one), in place of the subprojects it replaces. */
export interface BookMap {
  projects: Record<string, { to: string; replaces: string[] }>;
  /** Each of its subprojects' element (its `ref`): '' none. */
  refs: Record<string, string>;
}

/** Rows of a code each: a code on two rows (a row copied, not renumbered) is read from its first; the others are left out. */
const firstRows = (rows: BookRow[]): BookRow[] => rows.filter((r, i) => rows.findIndex((x) => x.code === r.code) === i);
/** The codes on more than one row of a workbook's KPI sheet: only the first row of each is read. */
export const twiceCodes = (book: PlanBook): string[] => [...new Set(book.rows.filter((r, i) => book.rows.findIndex((x) => x.code === r.code) !== i).map((r) => r.code))];

/** A proposal's subproject rows, by its project's number. */
const rowsOf = (book: PlanBook, nr: number): BookRow[] => firstRows(book.rows).filter((r) => r.code.split('.')[0] === String(nr));

/** The id a proposal's subproject gets: the same each time the same workbook comes, so it is found again. */
export function bookSubId(project: string, code: string): string {
  const slug = project.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 24);
  return `wb-${slug || 'p'}-${code}`;
}

/**
 * A proposal's project's short name when its KPI sheet has no "Projektas" column: the one it was given before, found by
 * its title. A part's of that title; else the project's of that title: its own, or its subprojects' that is no part's. '' none.
 */
function givenShort(book: PlanBook, bp: BookProject, c: Consolidation, plan: Record<string, unknown>, inst: string): string {
  if (!book.absent?.rows.includes('project')) return '';
  const pps = c.projects.map((p) => ({ p, pp: projectPlan(plan, inst, p.code) }));
  const part = pps.flatMap(({ pp }) => pp.parts ?? []).find((x) => x.short && partBack(x).title === bp.title);
  if (part) return part.short;
  const titled = (t?: string) => !!t && splitTitleGoal(readText(t)).title === bp.title;
  for (const { p, pp } of pps) {
    if (!titled(p.titleLt) && !titled(p.title)) continue;
    const own = pp.short || p.subs.map((x) => subPlan(plan, inst, x.id).part).find((x) => !!x && !pp.parts?.some((y) => y.short === x));
    if (own) return own;
  }
  return '';
}

/** A second element is replaced too when it nearly ties with the best on both scores. */
const NEAR = 0.75;

/**
 * A guess at where a proposal's projects go. Subprojects that came with one before are replaced again, where they are.
 * Otherwise its words (the rarer the more) are matched with each element's twice: its title and short name with the
 * element's titles, everything it says with everything the element says. The best on both together is replaced, and
 * a second one that nearly ties on both (a project that covers two). It goes to the project that holds the best.
 * Each of its subprojects refers to the chosen element it matches best.
 */
export function mergeProposal(book: PlanBook, c: Consolidation, els: PlanElement[], plan: Record<string, unknown>, inst: string): BookMap {
  const titles = new Map(els.map((e) => [e.id, stems(`${e.titleLt} ${e.title}`)]));
  const texts = new Map(els.map((e) => [e.id, stems([e.titleLt, e.title, ...e.actionsLt, ...e.actions, e.kpiLt, e.kpi].join(' '))]));
  const rareT = rarity([...titles.values()]);
  const rareX = rarity([...texts.values()]);
  const score = (doc: Set<string>, words: Map<string, Set<string>>, rare: (w: string) => number, id: string) => [...doc].reduce((n, w) => n + (words.get(id)?.has(w) ? rare(w) : 0), 0);
  const home = new Map<string, { code: string; sub: SubProject }[]>();
  for (const p of c.projects) for (const sub of p.subs) if (sub.ref) home.set(sub.ref, [...(home.get(sub.ref) ?? []), { code: p.code, sub }]);
  const out: BookMap = { projects: {}, refs: {} };
  for (const bp of book.projects) {
    const rows = rowsOf(book, bp.nr);
    const short = rows[0]?.project || givenShort(book, bp, c, plan, inst) || bp.title;
    const nr = String(bp.nr);
    const title = stems(`${bp.title} ${short}`);
    const all = stems([bp.title, bp.goal, short, ...bp.subs.map((x) => x.title), ...rows.flatMap((r) => [r.text, r.kpi])].join(' '));
    const ranked = els
      .filter((e) => home.has(e.id))
      .map((e) => ({ id: e.id, t: score(title, titles, rareT, e.id), x: score(all, texts, rareX, e.id) }));
    const topT = Math.max(0, ...ranked.map((r) => r.t)) || 1;
    const topX = Math.max(0, ...ranked.map((r) => r.x)) || 1;
    ranked.sort((a, b) => b.t / topT + b.x / topX - (a.t / topT + a.x / topX));
    const best = ranked[0];
    const chosen = best && best.t + best.x > 0 ? [best.id, ...ranked.slice(1).filter((r) => r.t >= NEAR * topT && r.x >= NEAR * topX).map((r) => r.id)] : [];
    const before = c.projects.flatMap((p) => p.subs.filter((x) => subPlan(plan, inst, x.id).part === short).map((x) => ({ code: p.code, id: x.id })));
    // One with no KPI rows brought no subprojects: found again by its short name, a project's or one of its parts'. It
    // replaces none: it has nothing to put in their place.
    const named = rows.length
      ? undefined
      : c.projects.find((p) => {
          const pp = projectPlan(plan, inst, p.code);
          return pp.short === short || !!pp.parts?.some((x) => x.short === short);
        });
    const replaces = before.length ? before.map((x) => x.id) : rows.length ? chosen.flatMap((id) => (home.get(id) ?? []).map((h) => h.sub.id)) : [];
    out.projects[nr] = { to: before[0]?.code ?? named?.code ?? home.get(chosen[0] ?? '')?.[0]?.code ?? '', replaces };
    for (const r of rows) {
      const doc = stems([bp.subs.find((x) => x.code === r.code)?.title ?? '', r.text, r.kpi].join(' '));
      out.refs[r.code] = [...chosen].sort((a, b) => score(doc, texts, rareX, b) - score(doc, texts, rareX, a))[0] ?? '';
    }
  }
  return out;
}

// ── merging and updating ────────────────────────────────────────────────────

/** What an import changes: the consolidation and the plan's entries (null: removed). */
export interface PlanResult {
  c: Consolidation;
  plan: Record<string, unknown>;
}

interface Ctx {
  inst: string;
  plan: Record<string, unknown>;
  by: string;
  now: Date;
  file: string;
  /** People by name (lower case): the email of the one a lead names. */
  people?: ReadonlyMap<string, string>;
  /** A new subproject's or project's id. */
  newId: () => string;
}

/** A Lithuanian text from the file over what was there: the same keeps its English; a new one keeps the old English, marked as maybe out of date. */
function lt<T extends Obj>(prev: T, field: string, value: string): Obj {
  const ltKey = `${field}Lt`;
  // Emptied: no change only when neither language has a text.
  if (value ? s(prev[ltKey]) === value : !prev[ltKey] && !prev[field]) return {};
  const mt = { ...(isObj(prev.mt) ? prev.mt : {}) };
  const old = { ...(isObj(prev.old) ? prev.old : {}) };
  delete mt[field];
  if (prev[field] && value) old[field] = 'en';
  else delete old[field];
  // Emptied in the file: gone in both languages.
  return { [ltKey]: value, ...(value ? {} : { [field]: '' }), mt, old };
}

/** `x` with Lithuanian texts from the file, one field after another (each keeps the others' marks). */
function lts<T extends Obj>(x: T, fields: Record<string, string>): T {
  const out: Obj = { ...x };
  for (const [f, v] of Object.entries(fields)) Object.assign(out, lt(out, f, v));
  return out as T;
}

/** Lists that say nothing when empty: no dependencies, no parts. Others (a project's `why`) stay, empty or not. */
const EMPTY_LISTS = new Set(['needs', 'parts']);

/** Empty texts and marks removed: no `mt: {}`, `old: {}` or `needs: []` left behind. */
function tidy<T extends Obj>(o: T): T {
  const out: Obj = {};
  for (const [k, v] of Object.entries(o)) {
    if (v === undefined || v === '' || (isObj(v) && !Object.keys(v).length) || (Array.isArray(v) && !v.length && EMPTY_LISTS.has(k))) continue;
    out[k] = v;
  }
  return out as T;
}

/** A part as its C cell reads back (readBack): a blank line holding spaces, or several, one blank line. */
const partBack = (y: { title: string; goal: string }): { title: string; goal: string } =>
  splitTitleGoal(readText(joinTitleGoal('·', '', [y]))).parts[0] ?? { title: readText(y.title), goal: readText(y.goal) };

/** A workbook project from the file: the English of `had` (the part it was) kept where its text is the same. */
function partFrom(short: string, x: { title: string; goal: string }, had?: PlanPart): PlanPart {
  const back = had && partBack(had);
  const title = !!back && back.title === x.title;
  const goal = !!back && back.goal === x.goal;
  return {
    short,
    title: title ? had!.title : x.title,
    goal: goal ? had!.goal : x.goal,
    ...(title && had!.titleEn ? { titleEn: had!.titleEn } : {}),
    ...(goal && had!.goalEn ? { goalEn: had!.goalEn } : {}),
  };
}

/**
 * A project's parts read again from its C cell, each matched to the part it was: by its title, else its goal. With as
 * many parts as before, one edited in both is the one at its place. Its short name stays, a new one has none.
 */
function partsFrom(read: { title: string; goal: string }[], before: PlanPart[]): PlanPart[] {
  const had: (PlanPart | undefined)[] = read.map(() => undefined);
  const left = new Set(before);
  const match = (same: (x: { title: string; goal: string }, y: PlanPart, i: number) => boolean) =>
    read.forEach((x, i) => {
      const y = had[i] ? undefined : [...left].find((y) => same(x, y, i));
      if (y) {
        had[i] = y;
        left.delete(y);
      }
    });
  const back = new Map(before.map((y) => [y, partBack(y)]));
  match((x, y) => !!x.title && back.get(y)!.title === x.title);
  match((x, y) => !!x.goal && back.get(y)!.goal === x.goal);
  if (read.length === before.length) match((_, y, i) => y === before[i]);
  return read.map((x, i) => partFrom(had[i]?.short ?? '', x, had[i]));
}

/**
 * A project's text composed from its parts (composed), cut back into each one's: from the paragraph starting with its
 * short name. Paragraphs before the first such are the project's own (''). None such: all `whole`'s, else all its own.
 */
function sections(t: string, shorts: string[], whole = ''): Map<string, string> {
  const out = new Map<string, string>();
  let at = '';
  for (const p of t ? t.split('\n\n') : []) {
    const short = shorts.find((x) => x && p.startsWith(`${x}: `));
    if (short) at = short;
    const v = short ? p.slice(short.length + 2) : p;
    out.set(at, out.has(at) ? `${out.get(at)}\n\n${v}` : v);
  }
  return whole && out.size === 1 && out.has('') ? new Map([[whole, t]]) : out;
}

/** Activities from their lines: the old ones kept where one is the same (their English stays). */
function actsFrom(lines: string[], before: Activity[] = []): Activity[] {
  return lines.map((x) => before.find((a) => (a.tLt || a.t) === x) ?? { t: '', tLt: x });
}

/** Activities as one text: sentences one after another, or a line each when one is no sentence. */
export function actsText(acts: { t: string; tLt?: string }[]): string {
  const ts = acts.map((a) => (a.tLt || a.t).trim()).filter(Boolean);
  return ts.every((x) => /[.!?…]$/.test(x)) ? ts.join(' ') : ts.join('\n');
}

/** An activity's line in the workbook's task cell. */
const ACT_LINE = /^[–-]\s+/;

/**
 * The workbook's task cell ("Uždavinys / veiksmai"): the subproject's goal, then its activities, a line each starting
 * with "– ". The original has the goal alone.
 */
export function splitTask(t: string): { goal: string; acts: string[] } {
  const lines = t.split('\n').map((l) => l.trim());
  return {
    goal: lines
      .filter((l) => !ACT_LINE.test(l))
      .join('\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim(),
    acts: lines.filter((l) => ACT_LINE.test(l)).map((l) => l.replace(ACT_LINE, '').trim()),
  };
}

/** The task cell of a goal and activities (splitTask reads it back). */
export const joinTask = (goal: string, acts: string[]): string => [goal.trim(), acts.map((a) => `– ${a}`).join('\n')].filter(Boolean).join('\n\n');

/** A KPI's name as a sentence takes it: before its "; peržiūra kas mėnesį", its first letter small unless an acronym's ("PathIS"). */
function kpiPhrase(k: string): string {
  const v = k.split(';')[0]!.trim().replace(/[.]$/, '');
  const first = v.split(/\s/)[0] ?? '';
  // A first word with other capitals is a name or an acronym ("PathIS", "IHC"): kept as it is.
  return /^\p{Lu}[^\p{Lu}]*$/u.test(first) ? v[0]!.toLowerCase() + v.slice(1) : v;
}

/**
 * A goal proposed from a subproject's KPI, in both languages, for one that has none: its final target and when
 * ("Iki 2030-12-31 pasiekti, kad automatizuotų prioritetinių sąsajų dalis būtų ≥ 100 %."), or, with targets in words
 * (an element's), those of each year. Empty in a language its KPI is not in.
 */
export function suggestedGoal(kpi: { en: string; lt: string }, sp: SubPlan, words?: { en: string[]; lt: string[] }): { en: string; lt: string } {
  const unit = sp.unitLt || sp.unit || '';
  const t = sp.t ?? [];
  const last = [...t.keys()].reverse().find((i) => t[i] != null);
  if (last !== undefined) {
    const value = `${sp.sign ? `${sp.sign} ` : ''}${planNumber(t[last], unit)}`;
    const year = CONSOLIDATION_YEARS[last]!;
    return {
      lt: kpi.lt ? `Iki ${sp.due || `${year} m.`} pasiekti, kad ${kpiPhrase(kpi.lt)} būtų ${value}.` : '',
      en: kpi.en ? `By ${sp.due || year}, ${kpiPhrase(kpi.en)} reaches ${value}.` : '',
    };
  }
  const yearly = (ts: string[], maintain: RegExp) =>
    ts
      .slice(0, CONSOLIDATION_YEARS.length)
      .map((x, i) => (x.trim() && !maintain.test(x.trim()) ? `${CONSOLIDATION_YEARS[i]} – ${x.trim().replace(/[.]$/, '')}` : ''))
      .filter(Boolean)
      .join('; ');
  const lt = yearly(words?.lt ?? [], /^(išlaikyti|-|–|—)$/i);
  const en = yearly(words?.en ?? [], /^(maintain|-|–|—)$/i);
  return {
    lt: kpi.lt ? (lt ? `Pasiekti, kad ${kpiPhrase(kpi.lt)} atitiktų metų tikslus: ${lt}.` : `Pasiekti ir išlaikyti siektiną rodiklio „${kpi.lt.split(';')[0]!.trim()}“ reikšmę.`) : '',
    en: kpi.en ? (en ? `Bring ${kpiPhrase(kpi.en)} to each year’s target: ${en}.` : `Reach and keep the target for “${kpi.en.split(';')[0]!.trim()}”.`) : '',
  };
}

/** Where a subproject's goal comes from: its own, the workbook's activities read before goals were kept (one text), or proposed from its KPI. */
export type GoalFrom = 'own' | 'workbook' | 'proposed' | 'none';

/**
 * A subproject's goal and activities as shown and exported. A subproject brought by a workbook before goals were kept
 * has the workbook's task text as its activities: that text is its goal, and it has no activities of its own.
 */
export function taskOf(sub: SubProject, sp: SubPlan, el?: PlanElement): { goal: { en: string; lt: string }; from: GoalFrom; acts: Activity[] } {
  const acts = sub.acts ?? (el?.actions ?? []).map((t, i) => ({ t, tLt: el?.actionsLt[i] }));
  if (sp.goal || sp.goalLt) return { goal: { en: sp.goal ?? '', lt: sp.goalLt ?? '' }, from: 'own', acts };
  if (sp.part && sub.acts?.length) return { goal: { en: sub.acts.every((a) => a.t) ? actsText(sub.acts.map((a) => ({ t: a.t }))) : '', lt: actsText(sub.acts.map((a) => ({ t: a.tLt || a.t }))) }, from: 'workbook', acts: [] };
  const kpi = sp.kpi || sp.kpiLt ? { en: sp.kpi ?? '', lt: sp.kpiLt ?? '' } : ownText(sub.kpi, sub.kpiLt, el?.kpi, el?.kpiLt);
  const goal = suggestedGoal(kpi, sp, el ? { en: el.targets, lt: el.targetsLt } : undefined);
  return { goal, from: goal.en || goal.lt ? 'proposed' : 'none', acts };
}

// ── projects and tasks within a direction ───────────────────────────────────
// People's words: a consolidated project is a Direction; in it each project (SubGroup: an element, VPC-02, or the
// workbook project its subprojects came with) holds Tasks (its subprojects). A project that is one subproject is that
// subproject itself. People make a task a project of its own, or a project a task of another (SubProject.grp).

/**
 * The project a subproject is a task of: where people put it (`grp`), else the workbook project it came with, else its
 * element; a new one of no element stands on its own.
 */
export function groupKeyOf(x: SubProject, plan: Record<string, unknown>, inst: string): string {
  if (x.grp) return x.grp;
  const short = subPlan(plan, inst, x.id).part;
  return short ? `part:${short}` : x.ref ? `ref:${x.ref}` : `new:${x.id}`;
}

/**
 * A project of a direction: the workbook project its subprojects came with (column C), else the element (VPC-07) they
 * are part of, else a subproject standing on its own (`own:`, `new:`). `whole`: one subproject that is the element
 * itself (no title of its own), or one on its own: shown as itself, with no tasks under it. `anchor`: the element it
 * is, which labels it (planLabels); none for a project on its own.
 */
export interface SubGroup {
  key: string;
  part?: PlanPart;
  /** The workbook project's short name, when it is one. */
  short?: string;
  refs: string[];
  anchor?: string;
  subs: SubProject[];
  whole: boolean;
}

/** A direction's subprojects by project, in the order they come. */
export function subGroups(p: ConsolidatedProject, plan: Record<string, unknown>, inst: string): SubGroup[] {
  const parts = projectPlan(plan, inst, p.code).parts ?? [];
  const out: SubGroup[] = [];
  for (const x of p.subs) {
    const key = groupKeyOf(x, plan, inst);
    let g = out.find((y) => y.key === key);
    if (!g) {
      const short = key.startsWith('part:') ? key.slice(5) : undefined;
      g = { key, ...(short ? { short, part: parts.find((y) => y.short === short) } : {}), refs: [], subs: [], whole: false };
      out.push(g);
    }
    g.subs.push(x);
    if (x.ref && !g.refs.includes(x.ref)) g.refs.push(x.ref);
  }
  for (const g of out) {
    const alone = g.key.startsWith('own:') || g.key.startsWith('new:');
    g.whole = !g.short && g.subs.length === 1 && (alone || !g.subs[0]!.ref || (!g.subs[0]!.title && !g.subs[0]!.titleLt));
    // Its element: the one it is named after; a workbook project's, that of the first subproject it came with.
    const anchor = g.key.startsWith('ref:') ? g.key.slice(4) : g.short ? (g.subs.find((x) => !x.grp)?.ref ?? g.refs[0]) : undefined;
    if (anchor) g.anchor = anchor;
  }
  return out;
}

/**
 * A project's title, in both languages: the workbook project's (Lithuanian; in English its element's, when one), else its
 * element's, else that of the subproject it is (one made a project of its own, or a new one).
 */
export function groupTitle(g: SubGroup, byId: ReadonlyMap<string, PlanElement>): { en: string; lt: string } {
  if (g.short) {
    const el = g.refs.length === 1 ? byId.get(g.refs[0]!) : undefined;
    return { lt: g.part?.title || g.short, en: g.part?.titleEn || el?.title || '' };
  }
  const el = g.anchor ? byId.get(g.anchor) : undefined;
  if (el) return { en: el.title, lt: el.titleLt };
  if (g.anchor) return { en: '', lt: '' };
  const id = g.key.slice(g.key.indexOf(':') + 1);
  const x = g.subs.find((y) => y.id === id) ?? g.subs[0];
  const xel = x?.ref ? byId.get(x.ref) : undefined;
  return x ? ownText(x.title, x.titleLt, xel?.title, xel?.titleLt) : { en: '', lt: '' };
}

/**
 * The labels people see in place of codes. A project's: the element it is (VPC-02). A task's: its project's, then its
 * number among that element's tasks across the directions, in the order shown (VPC-02.3); a project that is one
 * subproject is labelled as the project. A project on its own (a new one, a task made a project) and its tasks have none.
 * `project` by the direction's code and the project's key (`P1:part:…`), `sub` by subproject id.
 */
export function planLabels(c: Consolidation, plan: Record<string, unknown>, inst: string): { project: Map<string, string>; sub: Map<string, string> } {
  const project = new Map<string, string>();
  const sub = new Map<string, string>();
  const n = new Map<string, number>();
  for (const p of c.projects)
    for (const g of subGroups(p, plan, inst)) {
      if (!g.anchor) continue;
      project.set(`${p.code}:${g.key}`, g.anchor);
      if (g.whole) sub.set(g.subs[0]!.id, g.anchor);
      else
        for (const x of g.subs) {
          const k = (n.get(g.anchor) ?? 0) + 1;
          n.set(g.anchor, k);
          sub.set(x.id, `${g.anchor}.${k}`);
        }
    }
  return { project, sub };
}

/**
 * Subprojects `ids` made tasks of the project `key` in direction `to` (each from wherever it is), after its last task,
 * with their milestones; `grp` left unset where it is the one they would have anyway. `key` `own:<id>` with one id:
 * that one made a project of its own, where it is.
 */
export function regrouped(c: Consolidation, plan: Record<string, unknown>, inst: string, ids: readonly string[], to: string, key: string): Consolidation {
  const moving = new Set(ids);
  const set = (x: SubProject): SubProject => {
    const { grp: _g, ...rest } = x;
    return groupKeyOf(rest, plan, inst) === key ? rest : { ...rest, grp: key };
  };
  if (key.startsWith('own:') && ids.length === 1) return { ...c, projects: c.projects.map((p) => ({ ...p, subs: p.subs.map((x) => (moving.has(x.id) ? set(x) : x)) })) };
  const subs = c.projects.flatMap((p) => p.subs.filter((x) => moving.has(x.id)).map(set));
  const ms = c.projects.flatMap((p) => p.milestones.filter((m) => m.sub && moving.has(m.sub)));
  if (!subs.length || !c.projects.some((p) => p.code === to)) return c;
  return {
    ...c,
    projects: c.projects.map((p) => {
      const left = p.subs.filter((x) => !moving.has(x.id));
      const milestones = p.milestones.filter((m) => !m.sub || !moving.has(m.sub));
      if (p.code !== to) return { ...p, subs: left, milestones };
      // After the project's last task; at the end when none is left here.
      let at = -1;
      left.forEach((x, i) => groupKeyOf(x, plan, inst) === key && (at = i));
      const next = at < 0 ? [...left, ...subs] : [...left.slice(0, at + 1), ...subs, ...left.slice(at + 1)];
      return { ...p, subs: next, milestones: [...milestones, ...ms].sort((a, b) => a.y - b.y) };
    }),
  };
}

/** A label's place in a text (VPC-02.3, VU MF-01): not part of a longer one. */
const labelPattern = (labels: readonly string[]): RegExp | null =>
  labels.length ? new RegExp(`(?<![\\w.-])(${[...labels].sort((a, b) => b.length - a.length).map((l) => l.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})(?![\\w-]|\\.\\d)`, 'g') : null;

/** A text with each label it names (`ids`: label → subproject id) as {#id}, as tokenized() keeps codes: a label moves with its task. */
export function tokenizedLabels(t: string, ids: ReadonlyMap<string, string>): string {
  const re = labelPattern([...ids.keys()]);
  return re ? t.replace(re, (l) => `{#${ids.get(l)}}`) : t;
}

/**
 * Milestones of the year texts, a line each: one of `before` kept where its line is the same (its English, month and
 * deadline stay), else a new one matched to a subproject by its KPI's target that year. A line that names no target
 * goes with the line before it (the workbook lists them in its subprojects' order), the first with `fallback`.
 */
function yearMilestones(years: string[], rows: { id: string; row: BookRow }[], before: Milestone[], fallback: string): Milestone[] {
  const out: Milestone[] = [];
  const left = [...before];
  CONSOLIDATION_YEARS.forEach((y, i) => {
    let prev = fallback;
    for (const line of (years[i] ?? '').split('\n').map((x) => x.trim()).filter(Boolean)) {
      // Its text as the year's cell reads back (an export trims it, a CR LF is a line break).
      const k = left.findIndex((m) => m.y === y && readText(m.tLt || m.t) === line);
      if (k >= 0) {
        const had = left.splice(k, 1)[0]!;
        out.push(had);
        prev = had.sub ?? prev;
        continue;
      }
      const m = monthIn(line);
      const sub = lineSub(line, i, rows) || prev;
      prev = sub;
      out.push({ y, ...(m ? { m } : {}), t: '', tLt: line, ...(sub ? { sub } : {}) });
    }
  });
  return out;
}

/** The subproject a year's line is about: the one whose KPI has a target that year the line names, by its words when several. */
function lineSub(line: string, year: number, rows: { id: string; row: BookRow }[]): string {
  const nums = [...line.matchAll(/(\d+(?:[.,]\d+)?)\s*(%)?/g)].map((m) => ({ n: Number(m[1]!.replace(',', '.')), pct: !!m[2] }));
  const hits = rows.filter(({ row }) => {
    const v = row.t[year];
    if (v == null) return false;
    const pct = row.unit.trim() === '%';
    return nums.some((x) => x.pct === pct && Math.abs(x.n - (pct ? v * 100 : v)) < 1e-6);
  });
  if (hits.length <= 1) return hits[0]?.id ?? '';
  const w = stems(line);
  const shared = (r: BookRow) => [...stems(r.kpi)].filter((x) => w.has(x)).length;
  return [...hits].sort((a, b) => shared(b.row) - shared(a.row))[0]!.id;
}

/**
 * A subproject's plan from its row: the file's values over what was kept, the review's agreement and English kept where
 * unchanged. `was`: the row as the site would write it now (an export read back): only what differs from it is set,
 * so what the site shows from elsewhere (an element's KPI) is not stored as if typed. `absent`: columns the file lacks.
 */
function rowPlan(row: BookRow, prev: SubPlan, ids: ReadonlyMap<string, string>, extra: Partial<SubPlan>, was?: BookRow, absent: readonly string[] = []): SubPlan {
  const changed = <K extends keyof BookRow>(k: K) => !absent.includes(k) && (!was || JSON.stringify(row[k]) !== JSON.stringify(was[k]));
  const next: Obj = { ...prev, ...extra };
  const goalOf = (r: BookRow) => splitTask(r.text).goal;
  if (changed('text') && (!was || goalOf(row) !== goalOf(was))) Object.assign(next, lt(next, 'goal', goalOf(row)));
  if (changed('kpi')) Object.assign(next, lt(next, 'kpi', row.kpi));
  if (changed('def')) Object.assign(next, lt(next, 'def', row.def));
  if (changed('baseNote')) Object.assign(next, lt(next, 'baseNote', row.baseNote));
  if (changed('unit')) {
    Object.assign(next, lt(next, 'unit', row.unit));
    if (row.unit.trim() === '%') next.unit = '%';
  }
  if (changed('base')) next.base = row.base;
  if (changed('sign')) next.sign = row.sign || undefined;
  if (changed('t')) next.t = row.t;
  if (changed('due')) next.due = row.due || undefined;
  if (changed('deps')) {
    // The text as written, its codes kept as the subprojects they name; what it starts with is what the subproject needs.
    next.needs = leadingCodes(row.deps).codes.map((c) => ids.get(c)).filter((x): x is string => !!x);
    Object.assign(next, lt(next, 'cond', tokenized(row.deps, ids)));
  }
  // A changed value is a new proposal: agreed again in the review.
  if (valuesChanged(prev, next as SubPlan)) delete next.ok;
  return tidy(next) as SubPlan;
}

/** A lead from the file: the name, with the email of the person of that name. Empty: what was set stays. */
function withLead<T extends { lead?: string; leadEmail?: string }>(x: T, name: string, people?: ReadonlyMap<string, string>): T {
  if (!name || name === x.lead) return x;
  const email = people?.get(name.toLowerCase());
  const { leadEmail: _e, ...rest } = x;
  return { ...rest, lead: name, ...(email ? { leadEmail: email } : {}) } as T;
}

/** The workbook's own texts over the stored ones (one it leaves empty stays), with the file it came from. */
function metaFrom(book: PlanBook, ctx: Ctx): PlanMeta {
  const texts = { title: book.title, notes: book.notes, kpiTitle: book.kpiTitle, kpiNotes: book.kpiNotes, blocks: book.blocks, footer: book.footer };
  const brought = Object.entries(texts).filter(([, v]) => (Array.isArray(v) ? v.length > 0 : !!v));
  return tidy({ ...planMeta(ctx.plan, ctx.inst), ...Object.fromEntries(brought), file: ctx.file, at: ctx.now.toISOString(), by: ctx.by });
}

/**
 * A project's texts composed from the parts merged into it: each prefixed with its short name when there are several, or
 * other subprojects. The project's own text (short name '') first, with none.
 */
function composed(parts: { short: string; text: string }[], prefix: boolean): string {
  const ps = [...parts.filter((p) => !p.short), ...parts.filter((p) => p.short)].filter((p) => p.text);
  return ps.map((p) => (prefix && p.short ? `${p.short}: ${p.text}` : p.text)).join('\n\n');
}

/**
 * A proposal merged into the consolidation as `map` says: each of its projects into its consolidated project (a new
 * one for ''), its subprojects where the first one it replaces was, those it replaces gone with their milestones; its
 * year targets as milestones; its KPIs, deadlines and dependencies in the plan.
 */
export function mergeBook(book: PlanBook, map: BookMap, c: Consolidation, ctx: Ctx): PlanResult {
  const plan: Record<string, unknown> = {};
  let projects = c.projects.map((p) => ({ ...p, subs: [...p.subs], milestones: [...p.milestones] }));
  const ids = new Map<string, string>();
  // With no "Projektas" column, each project's short name as given before: its subprojects keep their ids.
  const given = new Map(book.projects.map((bp) => [bp, givenShort(book, bp, c, ctx.plan, ctx.inst)] as const));
  for (const r of firstRows(book.rows)) {
    const bp = book.projects.find((p) => r.code.startsWith(`${p.nr}.`));
    ids.set(r.code, bookSubId(r.project || (bp && given.get(bp)) || bp?.title || '', r.code));
  }
  const oldSubs = new Map(c.projects.flatMap((p) => p.subs.map((x) => [x.id, x] as const)));
  // A milestone of a subproject the workbook brings again: kept when its line is (its English stays).
  const brought = new Set(ids.values());
  const oldMs = c.projects.flatMap((p) => p.milestones).filter((m) => !!m.sub && brought.has(m.sub));
  // The new projects first: a proposal's project with nowhere to go, or going to a project `c` does not have (a map
  // made for another institution's projects).
  const fresh = new Set<string>();
  // Each entry once: two rows of one Nr. (a row copied, not renumbered) share it, so one new project.
  const seen = new Set<BookMap['projects'][string]>();
  for (const bp of book.projects) {
    const m = map.projects[String(bp.nr)];
    if (!m || seen.has(m)) continue;
    seen.add(m);
    if (!m.to || !c.projects.some((p) => p.code === m.to)) {
      const n = Math.max(0, ...projects.map((p) => Number(/^P(\d+)$/.exec(p.code)?.[1]) || 0)) + 1;
      m.to = `P${n}`;
      fresh.add(m.to);
      projects.push({ code: m.to, title: '', titleLt: bp.title, purposeLt: bp.goal, why: [], subs: [], milestones: [] });
    }
  }
  // A column the workbook lacks says nothing: what it would hold stays as it is.
  const absent = book.absent?.rows ?? [];
  const partsOf = new Map<string, { short: string; bp: BookProject }[]>();
  // The milestones this workbook's projects kept so far.
  const claimed = new Set<Milestone>();
  for (const bp of book.projects) {
    const m = map.projects[String(bp.nr)];
    if (!m) continue;
    const rows = rowsOf(book, bp.nr);
    const short = rows[0]?.project || given.get(bp) || bp.title;
    partsOf.set(m.to, [...(partsOf.get(m.to) ?? []), { short, bp }]);
    const subs: SubProject[] = rows.map((r) => {
      const id = ids.get(r.code)!;
      const had = oldSubs.get(id);
      const task = splitTask(r.text);
      // No line in the E cell: its task's first sentence, else its code; with no task column, the title it has.
      const title = bp.subs.find((x) => x.code === r.code)?.title ?? (had && absent.includes('text') ? '' : (sentences(task.goal)[0] ?? r.code));
      const base: SubProject = had ? { ...had } : { id, s: 9, e: r.due ? dueMonth(r.due) : 59 };
      const ref = map.refs[r.code];
      const sp = subPlan(ctx.plan, ctx.inst, id);
      // Brought by a workbook before goals were kept: its activities are the task text, now its goal.
      const legacy = !!sp.part && !sp.goal && !sp.goalLt && !!had?.acts?.length;
      // Its activities: the task cell's "– " lines. With none (the original has none: its text is the goal), the ones
      // added on the site stay.
      const keep = !!had?.acts && (absent.includes('text') || (!task.acts.length && !legacy));
      const next: SubProject = {
        ...base,
        ...(ref ? { ref } : {}),
        ...((title ? lt(base as unknown as Obj, 'title', title) : {}) as Partial<SubProject>),
        acts: keep ? had!.acts! : actsFrom(task.acts, legacy ? [] : had?.acts),
      };
      if (!ref) delete next.ref;
      return withLead(tidy(next as unknown as Obj) as unknown as SubProject, r.lead, ctx.people);
    });
    const gone = new Set([...m.replaces, ...subs.map((x) => x.id)]);
    const target = projects.find((p) => p.code === m.to)!;
    const at = target.subs.findIndex((x) => gone.has(x.id));
    projects = projects.map((p) => ({ ...p, subs: p.subs.filter((x) => !gone.has(x.id)), milestones: p.milestones.filter((x) => !x.sub || !gone.has(x.sub)) }));
    const t = projects.find((p) => p.code === m.to)!;
    t.subs.splice(at < 0 ? t.subs.length : Math.min(at, t.subs.length), 0, ...subs);
    // One with no KPI rows: its year lines are milestones of no subproject, kept where their line is, not doubled. Those
    // of a project it was in before (its short name there) go with it, unless another of this workbook's kept them.
    const wasIn = (code: string) => {
      const pp = projectPlan(ctx.plan, ctx.inst, code);
      return code !== m.to && !fresh.has(code) && (pp.short === short || !!pp.parts?.some((x) => x.short === short));
    };
    const loose = rows.length ? [] : [...t.milestones, ...projects.filter((p) => wasIn(p.code)).flatMap((p) => p.milestones.filter((x) => !claimed.has(x)))].filter((x) => !x.sub);
    // Its own subprojects' only: a line the same in two of its projects stays each one's.
    const own = new Set(subs.map((x) => x.id));
    const ms = yearMilestones(bp.years, rows.map((r) => ({ id: ids.get(r.code)!, row: r })), [...loose, ...oldMs.filter((x) => own.has(x.sub!))], subs[0]?.id ?? '');
    t.milestones = [...t.milestones.filter((x) => !ms.includes(x)), ...ms].sort((a, b) => a.y - b.y);
    for (const x of ms) claimed.add(x);
    projects = projects.map((p) => (p.code === m.to ? withLead(p, bp.lead, ctx.people) : { ...p, milestones: p.milestones.filter((x) => !ms.includes(x)) }));
    for (const r of rows) {
      const id = ids.get(r.code)!;
      plan[planKey(ctx.inst, id)] = rowPlan(r, subPlan(ctx.plan, ctx.inst, id), ids, { part: short, from: r.code }, undefined, absent);
    }
  }
  // Each project's texts from its parts: the ones this workbook brings replaced; another workbook's kept while it is
  // there (a subproject of it left, or it never had one); its own (no short name before it) kept. In the order they
  // came, new ones last.
  const partOf = (id: string) => subPlan(ctx.plan, ctx.inst, id).part;
  const sent = new Set([...partsOf.values()].flatMap((ps) => ps.map((x) => x.short)).filter(Boolean));
  // Also a project one of this workbook's projects left (sent to another one this time): its part and texts go with it.
  const left = c.projects
    .filter((p) => {
      const pp = projectPlan(ctx.plan, ctx.inst, p.code);
      return !partsOf.has(p.code) && (sent.has(pp.short ?? '') || !!pp.parts?.some((x) => sent.has(x.short)) || p.subs.some((x) => sent.has(partOf(x.id) ?? '')));
    })
    .map((p) => p.code);
  for (const code of [...partsOf.keys(), ...left]) {
    const parts = partsOf.get(code) ?? [];
    const p = projects.find((x) => x.code === code)!;
    const mine = new Set(parts.flatMap(({ bp }) => rowsOf(book, bp.nr).map((r) => ids.get(r.code))));
    // A new project starts with no texts: an entry still under its code was another project's.
    const prev: ProjectPlan = fresh.has(code) ? {} : projectPlan(ctx.plan, ctx.inst, code);
    const had = c.projects.find((x) => x.code === code)?.subs ?? [];
    // One of this workbook's sent to another project is here only while a subproject of it is left.
    const elsewhere = (short: string) => sent.has(short) && !parts.some((x) => x.short === short);
    const stays = (short: string) => p.subs.some((x) => !mine.has(x.id) && partOf(x.id) === short) || (!elsewhere(short) && !had.some((x) => partOf(x.id) === short));
    // Its short name (a project made from one) and its parts', then this workbook's.
    const known = [...new Set([...(prev.short ? [prev.short] : []), ...(prev.parts ?? []).map((x) => x.short), ...parts.map((x) => x.short)])];
    const fields = ['vulsk', 'state', 'resources'] as const;
    const kept = new Map(fields.map((f) => [f, sections(s((prev as Obj)[`${f}Lt`]), known, prev.short)] as const));
    // Its own text (typed on the site, or before the first short name): it stays.
    const own = [...kept.values()].some((k) => !!k.get(''));
    const units = [...known, ...(own && !known.includes('') ? [''] : [])].flatMap((short): { short: string; bp?: BookProject }[] => {
      const bs = parts.filter((x) => x.short === short);
      return bs.length ? bs : !short || stays(short) ? [{ short }] : [];
    });
    const prefix = units.length > 1 || p.subs.some((x) => !mine.has(x.id));
    const next: Obj = { ...prev };
    for (const f of fields) {
      if ((book.absent?.projects ?? []).some((x) => x === f)) continue;
      Object.assign(next, lt(next, f, composed(units.map(({ short, bp }) => ({ short, text: bp ? bp[f] : (kept.get(f)!.get(short) ?? '') })), prefix)));
    }
    // A part that is the project itself (a new one made from it) is no part: its title and goal are the project's.
    next.parts = units.flatMap(({ short, bp }) => {
      const was = prev.parts?.filter((x) => x.short === short) ?? [];
      if (!bp) return was;
      return bp.title !== (p.titleLt || p.title) ? [partFrom(short, bp, was.find((x) => partBack(x).title === bp.title) ?? was[0])] : [];
    });
    if (units.length === 1 && !prefix) next.short = units[0]!.short;
    // The short name of one gone to another project: no longer its.
    else if (prev.short && elsewhere(prev.short) && !units.some((x) => x.short === prev.short)) delete next.short;
    plan[planKey(ctx.inst, code)] = tidy(next);
  }
  plan[planKey(ctx.inst)] = metaFrom(book, ctx);
  return { c: { ...c, projects }, plan };
}

/** The month (0–59) a deadline falls in. */
export const dueMonth = (due: string): number => {
  const m = /^(20\d\d)-(\d\d)/.exec(due);
  return m ? Math.max(0, Math.min(59, (Number(m[1]) - CONSOLIDATION_YEARS[0]) * 12 + Number(m[2]) - 1)) : 59;
};

/** A workbook laid out as the consolidation: as many projects, the same titles in the same order (an export, edited). */
export function isSameLayout(book: PlanBook, c: Consolidation): boolean {
  // A project's title as its C cell reads back.
  const same = (a: string, b?: string) => !!b && a.trim().toLowerCase() === splitTitleGoal(readText(b)).title.toLowerCase();
  return book.projects.length === c.projects.length && book.projects.every((bp, i) => same(bp.title, c.projects[i]!.titleLt) || same(bp.title, c.projects[i]!.title));
}

/**
 * A workbook as its export reads back (web planXlsx.ts, then readPlanBook): each text as its cell holds it and the
 * reader takes it, the C and E cells split again, a block's item with no text a heading or a closing line.
 */
function readBack(b: PlanBook): PlanBook {
  const cells = (rows: (string | null)[][]): Cell[][] => rows.map((r) => r.map((v) => (v === null ? null : cellChars(v))));
  // A sheet's top: its title, then a row a note.
  const top = (title: string, notes: string[]) => topOf(cells([[title], ...notes.map((n) => [n])]), notes.length + 1);
  const plan = top(b.title, b.notes);
  const kpi = top(b.kpiTitle, b.kpiNotes);
  const below = blocksOf(cells([...b.blocks.flatMap((x) => [...(x.heading ? [[x.heading]] : []), ...x.items.map((it) => [it.label, null, it.text])]), ...b.footer.map((f) => [f])]));
  return {
    ...b,
    ...plan,
    projects: b.projects.map((p) => {
      const tg = splitTitleGoal(readText(joinTitleGoal(p.title, p.goal, p.parts)));
      return { ...p, vulsk: readText(p.vulsk), ...tg, lead: readText(p.lead), subs: subsOf(readText(joinSubs(p.subs))), state: readText(p.state), years: p.years.map(readText), resources: readText(p.resources) };
    }),
    ...below,
    kpiTitle: kpi.title,
    kpiNotes: kpi.notes,
    rows: b.rows.map((r) => ({ ...r, project: readText(r.project), text: readText(r.text), lead: readText(r.lead), kpi: readText(r.kpi), def: readText(r.def), baseNote: readText(r.baseNote), unit: readText(r.unit), deps: readText(r.deps) })),
  };
}

/** A workbook with the cells of the columns it lacks as `was` has them (the export as the site writes it): they say nothing. */
function present(book: PlanBook, was: PlanBook): PlanBook {
  if (!book.absent) return book;
  const { projects: pf, rows: rf } = book.absent;
  const wasRows = new Map(was.rows.map((r) => [r.code, r]));
  return {
    ...book,
    projects: book.projects.map((p, i) => ({ ...p, ...Object.fromEntries(pf.map((f) => [f, was.projects[i]?.[f] ?? p[f]])) })),
    rows: book.rows.map((r) => ({ ...r, ...Object.fromEntries(rf.map((f) => [f, wasRows.get(r.code)?.[f] ?? r[f]])) })),
  };
}

/**
 * An export, edited, read back: each project and subproject by its place. Each cell is compared with what the site
 * writes there now (bookOf), as the file reads it back: only the ones edited change, so a value the site shows from
 * elsewhere (an element's title or KPI, the VULSK goals of its links) stays where it comes from. A row the file adds
 * is a new subproject; one it leaves out is kept. A column it lacks changes nothing.
 */
export function updateBook(file: PlanBook, c: Consolidation, ctx: Ctx & { content: Obj; els: PlanElement[] }): PlanResult {
  const plan: Record<string, unknown> = {};
  const was = readBack(bookOf(c, ctx.plan, ctx.content, ctx.els, ctx.inst));
  const book = present(file, was);
  const wasRows = new Map(was.rows.map((r) => [r.code, r]));
  const codes = subCodes(c);
  const ids = new Map([...codes].map(([id, code]) => [code, id] as const));
  const rowsByCode = new Map(firstRows(book.rows).map((r) => [r.code, r]));
  // New rows first: their ids are needed by the dependencies of others.
  for (const r of book.rows) if (!ids.has(r.code) && c.projects[Number(r.code.split('.')[0]) - 1]) ids.set(r.code, ctx.newId());
  // Each subproject's row by the code the file gives it: a new one's place need not be its code (2.3 after 2.1).
  const fileCode = new Map([...ids].map(([code, id]) => [id, code] as const));
  const projects = c.projects.map((p, pi) => {
    const bp = book.projects[pi]!;
    const wp = was.projects[pi]!;
    const subs = p.subs.map((x) => ({ ...x }));
    // New rows in the order of their codes, named as a merge names them: the E cell's line, else the task's first sentence.
    const order = (r: BookRow) => Number(r.code.split('.')[1]);
    for (const r of book.rows.filter((x) => x.code.startsWith(`${pi + 1}.`)).sort((a, b) => order(a) - order(b))) {
      const id = ids.get(r.code)!;
      if (subs.some((x) => x.id === id)) continue;
      subs.push({ id, titleLt: bp.subs.find((y) => y.code === r.code)?.title ?? sentences(splitTask(r.text).goal)[0] ?? r.code, s: 9, e: r.due ? dueMonth(r.due) : 59 });
    }
    const next = subs.map((x, si) => {
      const code = fileCode.get(x.id) ?? `${pi + 1}.${si + 1}`;
      const r = rowsByCode.get(code);
      const w = wasRows.get(code);
      const title = bp.subs.find((y) => y.code === code)?.title;
      let out: SubProject = { ...x };
      if (title && title !== wp.subs.find((y) => y.code === code)?.title) Object.assign(out, lt(out as unknown as Obj, 'title', title));
      if (r) {
        const had = subPlan(ctx.plan, ctx.inst, x.id);
        // Brought by a workbook before goals were kept: its activities are the task text, now its goal.
        const legacy = !!had.part && !had.goal && !had.goalLt && !!x.acts?.length;
        const now = splitTask(r.text);
        const edited = r.text !== (w?.text ?? '');
        // The old ones as shown (an element's when it has none of its own; none for one of the legacy): their English stays.
        if (edited && (legacy || JSON.stringify(now.acts) !== JSON.stringify(splitTask(w?.text ?? '').acts))) out.acts = actsFrom(now.acts, taskOf(x, had, ctx.els.find((e) => e.id === x.ref)).acts);
        if (r.lead !== (w?.lead ?? '')) out = r.lead ? withLead(out, r.lead, ctx.people) : (({ lead: _l, leadEmail: _e, ...rest }) => rest)(out);
        // One of the legacy, its text edited: the goal it showed is its own from now on, both languages (as the site keeps
        // it when an activity is added); a goal edited too keeps its English, marked as maybe out of date.
        const shown = legacy && edited ? { ...had, goal: taskOf(x, had).goal.en, goalLt: splitTask(w?.text ?? '').goal } : had;
        const sp = rowPlan(r, shown, ids, {}, w);
        if (Object.keys(sp).length || planKey(ctx.inst, x.id) in ctx.plan) plan[planKey(ctx.inst, x.id)] = sp;
      }
      return tidy(out as unknown as Obj) as unknown as SubProject;
    });
    const rows = next.flatMap((x, si) => {
      const r = rowsByCode.get(fileCode.get(x.id) ?? `${pi + 1}.${si + 1}`);
      return r ? [{ id: x.id, row: r }] : [];
    });
    // A year whose text is as the site writes it keeps its milestones as they are; an edited one is read line by line.
    // One of another year (kept before milestones were the plan's years only) stays as it is.
    const ms = [
      ...CONSOLIDATION_YEARS.flatMap((y, i) => {
        const mine = p.milestones.filter((m) => m.y === y);
        if ((bp.years[i] ?? '') === (wp.years[i] ?? '')) return mine;
        return yearMilestones(CONSOLIDATION_YEARS.map((_, j) => (j === i ? (bp.years[i] ?? '') : '')), rows, mine, next[0]?.id ?? '');
      }),
      ...p.milestones.filter((m) => !(CONSOLIDATION_YEARS as readonly number[]).includes(m.y)),
    ].sort((a, b) => a.y - b.y);
    const prev = projectPlan(ctx.plan, ctx.inst, p.code);
    const pp: Obj = { ...prev };
    for (const f of ['vulsk', 'state', 'resources'] as const) if (bp[f] !== wp[f]) Object.assign(pp, lt(pp, f, bp[f]));
    if (JSON.stringify(bp.parts) !== JSON.stringify(wp.parts)) pp.parts = partsFrom(bp.parts, prev.parts ?? []);
    if (Object.keys(tidy(pp)).length || planKey(ctx.inst, p.code) in ctx.plan) plan[planKey(ctx.inst, p.code)] = tidy(pp);
    let proj = { ...p, subs: next, milestones: ms } as ConsolidatedProject;
    if (bp.title !== wp.title) proj = lts(proj as unknown as Obj, { title: bp.title }) as unknown as ConsolidatedProject;
    if (bp.goal !== wp.goal) proj = lts(proj as unknown as Obj, { purpose: bp.goal }) as unknown as ConsolidatedProject;
    if (bp.lead !== wp.lead) proj = bp.lead ? withLead(proj, bp.lead, ctx.people) : (({ lead: _l, leadEmail: _e, ...rest }) => rest as ConsolidatedProject)(proj);
    return tidy(proj as unknown as Obj) as unknown as ConsolidatedProject;
  });
  const meta = planMeta(ctx.plan, ctx.inst);
  const texts = { title: book.title, notes: book.notes, kpiTitle: book.kpiTitle, kpiNotes: book.kpiNotes, blocks: book.blocks, footer: book.footer };
  const wasTexts = { title: was.title, notes: was.notes, kpiTitle: was.kpiTitle, kpiNotes: was.kpiNotes, blocks: was.blocks, footer: was.footer };
  const edited = Object.fromEntries(Object.entries(texts).filter(([k, v]) => JSON.stringify(v) !== JSON.stringify(wasTexts[k as keyof typeof wasTexts])));
  plan[planKey(ctx.inst)] = tidy({ ...meta, ...edited, file: ctx.file, at: ctx.now.toISOString(), by: ctx.by });
  return { c: { ...c, projects }, plan };
}

/** The plan's changes: each entry that differs from what is saved. */
export function planChanges(saved: Record<string, unknown>, next: Record<string, unknown>): StrategyChange[] {
  return Object.entries(next)
    .filter(([k, v]) => JSON.stringify(saved[k] ?? null) !== JSON.stringify(v ?? null))
    .map(([key, value]) => ({ section: 'plan' as const, key, value: value ?? null }));
}

// ── what an import changes, in words ────────────────────────────────────────

/** What a line of an import's changes is about. */
export type PlanField =
  | 'project'
  | 'subproject'
  | 'code'
  | 'milestones'
  | 'texts'
  | 'title'
  | 'goal'
  | 'purpose'
  | 'lead'
  | 'vulsk'
  | 'state'
  | 'resources'
  | 'activities'
  | 'kpi'
  | 'definition'
  | 'baseline'
  | 'baselineNote'
  | 'sign'
  | 'unit'
  | 'targets'
  | 'deadline'
  | 'conditions'
  | 'needs';

export interface PlanDiffLine {
  kind: 'add' | 'remove' | 'change';
  /** A project's code (P3), a subproject's code (3.2, its place after the import), or the institution (its texts). */
  where: string;
  field: PlanField;
  /** A subproject's or project's title. */
  name?: string;
  /** How many (milestones). */
  n?: number;
  from?: string;
  to?: string;
}

const yearsWord = (t?: (number | null)[], unit = '') => (t ?? []).map((v, i) => (v == null ? '' : `${CONSOLIDATION_YEARS[i]}: ${planNumber(v, unit)}`)).filter(Boolean).join(', ');

/** What a result changes, project by project: subprojects added, removed, renamed; milestones; KPI values and texts. */
export function planDiff(before: Consolidation, beforePlan: Record<string, unknown>, after: PlanResult, inst: string): PlanDiffLine[] {
  const out: PlanDiffLine[] = [];
  const codesA = subCodes(after.c);
  const codesB = subCodes(before);
  const afterIds = new Set(codesA.keys());
  const name = (x: SubProject) => (x.titleLt || x.title || x.ref || x.id).slice(0, 90);
  const short = (t?: string) => {
    const v = (t ?? '').replace(/\s+/g, ' ').trim();
    return v.length > 90 ? `${v.slice(0, 89)}…` : v;
  };
  const texts = (where: string, pairs: [PlanField, string | undefined, string | undefined][]) => {
    for (const [field, from, to] of pairs) if ((from ?? '') !== (to ?? '')) out.push({ kind: 'change', where, field, from: short(from), to: short(to) });
  };
  for (const p of before.projects) for (const x of p.subs) if (!afterIds.has(x.id)) out.push({ kind: 'remove', where: codesB.get(x.id)!, field: 'subproject', name: name(x) });
  for (const p of after.c.projects) {
    const was = before.projects.find((q) => q.code === p.code);
    if (!was) out.push({ kind: 'add', where: p.code, field: 'project', name: p.titleLt || p.title });
    else {
      const a = projectPlan(beforePlan, inst, p.code);
      const b = (after.plan[planKey(inst, p.code)] as ProjectPlan | undefined) ?? a;
      texts(p.code, [
        ['title', was.titleLt || was.title, p.titleLt || p.title],
        ['purpose', was.purposeLt || was.purpose, p.purposeLt || p.purpose],
        ['lead', was.lead, p.lead],
        ['vulsk', a.vulskLt || a.vulsk, b.vulskLt || b.vulsk],
        ['state', a.stateLt || a.state, b.stateLt || b.state],
        ['resources', a.resourcesLt || a.resources, b.resourcesLt || b.resources],
      ]);
      // The workbook projects merged into it: each one's title and goal, by its short name; one gone, then.
      const left = [...(a.parts ?? [])];
      const pairs = (b.parts ?? []).map((y): [PlanPart | undefined, PlanPart] => {
        const k = left.findIndex((x) => x.short === y.short);
        return [k >= 0 ? left.splice(k, 1)[0] : undefined, y];
      });
      for (const [x, y] of [...pairs, ...left.map((x): [PlanPart, undefined] => [x, undefined])]) {
        const part = short(y?.short || x?.short || y?.title || x?.title);
        for (const f of ['title', 'goal'] as const) if ((x?.[f] ?? '') !== (y?.[f] ?? '')) out.push({ kind: 'change', where: p.code, field: f, name: part, from: short(x?.[f]), to: short(y?.[f]) });
      }
    }
    for (const x of p.subs) {
      const code = codesA.get(x.id)!;
      const had = codesB.get(x.id);
      if (!had) {
        out.push({ kind: 'add', where: code, field: 'subproject', name: name(x) });
        continue;
      }
      if (had !== code) out.push({ kind: 'change', where: code, field: 'code', name: name(x), from: had, to: code });
      const wasSub = before.projects.flatMap((q) => q.subs).find((y) => y.id === x.id)!;
      const a = subPlan(beforePlan, inst, x.id);
      const b = (after.plan[planKey(inst, x.id)] as SubPlan | undefined) ?? a;
      const unit = b.unitLt || b.unit || '';
      texts(code, [
        ['title', wasSub.titleLt, x.titleLt],
        ['goal', a.goalLt || a.goal, b.goalLt || b.goal],
        ['activities', actsText(wasSub.acts ?? []), actsText(x.acts ?? [])],
        ['lead', wasSub.lead, x.lead],
        ['kpi', a.kpiLt || a.kpi, b.kpiLt || b.kpi],
        ['definition', a.defLt || a.def, b.defLt || b.def],
        ['baseline', planNumber(a.base, unit), planNumber(b.base, unit)],
        ['baselineNote', a.baseNoteLt || a.baseNote, b.baseNoteLt || b.baseNote],
        ['sign', a.sign, b.sign],
        ['unit', a.unitLt || a.unit, b.unitLt || b.unit],
        ['targets', yearsWord(a.t, unit), yearsWord(b.t, unit)],
        ['deadline', a.due, b.due],
        ['conditions', a.condLt || a.cond, b.condLt || b.cond],
        ['needs', (a.needs ?? []).map((id) => codesB.get(id) ?? '?').join(', '), (b.needs ?? []).map((id) => codesA.get(id) ?? '?').join(', ')],
      ]);
    }
    const ms = (q?: ConsolidatedProject) => new Set((q?.milestones ?? []).map((m) => `${m.y} ${m.tLt || m.t}`));
    const a = ms(was);
    const b = ms(p);
    const added = [...b].filter((x) => !a.has(x));
    const removed = [...a].filter((x) => !b.has(x));
    if (added.length) out.push({ kind: 'add', where: p.code, field: 'milestones', n: added.length });
    if (removed.length) out.push({ kind: 'remove', where: p.code, field: 'milestones', n: removed.length });
  }
  const ma = planMeta(beforePlan, inst);
  const mb = (after.plan[planKey(inst)] as PlanMeta | undefined) ?? ma;
  const metaText = (m: PlanMeta) => JSON.stringify([m.title, m.notes, m.kpiTitle, m.kpiNotes, m.blocks, m.footer]);
  if (metaText(ma) !== metaText(mb)) out.push({ kind: 'change', where: inst, field: 'texts' });
  return out;
}

// ── the workbook of a consolidation ─────────────────────────────────────────

/** The VULSK directions, goals and strategic projects a project's elements serve, as the workbook's B column words them. */
export function vulskOf(content: Obj, refs: string[]): string {
  const ext = new Map(list(content.external).map((x) => [s(x.id), x]));
  const projects = new Map(list(content.projects).map((x) => [s(x.id), x]));
  const dirs = new Map<string, { goals: Map<string, Set<string>> }>();
  for (const ref of refs) {
    const e = projects.get(ref);
    const goal = ext.get(s(e?.goal));
    if (!goal) continue;
    const dir = s(goal.parent);
    if (!dirs.has(dir)) dirs.set(dir, { goals: new Map() });
    const g = dirs.get(dir)!.goals;
    if (!g.has(s(goal.id))) g.set(s(goal.id), new Set());
    const prog = ext.get(s(e?.prog));
    if (prog) g.get(s(goal.id))!.add(s(prog.title));
  }
  const ROMAN: Record<string, number> = { I: 1, II: 2, III: 3, IV: 4, V: 5, VI: 6 };
  return [...dirs]
    .map(([dir, { goals }]) => {
      const d = ext.get(dir);
      const roman = s(d?.code) || dir.replace(/^.*-/, '');
      const lines = [`${roman} kryptis. ${s(d?.title)}`.trim()];
      for (const [gid, progs] of goals) {
        const g = ext.get(gid)!;
        const [r, n] = s(g.code).split('.');
        lines.push(`${ROMAN[r ?? ''] ?? r}.${n}. ${s(g.title)}`);
        if (progs.size) lines.push(`Susiję projektai: ${[...progs].join('; ')}.`);
      }
      return lines.join('\n');
    })
    .join('\n\n');
}

/** The VULSK goals a B text names ("2.2.", "1.2 tikslas"): their ids, the content's only. */
export function vulskGoals(content: Obj, t: string): string[] {
  const ROMAN = ['', 'I', 'II', 'III', 'IV', 'V', 'VI'];
  const known = new Set(list(content.external).map((x) => s(x.id)));
  const out = new Set<string>();
  for (const m of t.matchAll(/(?<![\d.])([1-6])\.([1-9])(?!\d)/g)) {
    const id = `VULSK-${ROMAN[Number(m[1])]}.${m[2]}`;
    if (known.has(id)) out.add(id);
  }
  return [...out];
}

/** An element's field from the content, Lithuanian (the content's own language). */
const elementText = (content: Obj, id: string, key: string): string => s(list(content.projects).find((x) => x.id === id)?.[key] ?? list(content.external).find((x) => x.id === id)?.[key]);

/**
 * The workbook of a consolidation: its projects and subprojects in order, with what the plan keeps; what the plan
 * lacks from the elements (their KPI, the VULSK goals they serve). Lithuanian, as the workbook is.
 */
/** A subproject's task cell: its goal (its own, the workbook's, or proposed), then its activities. Lithuanian first. */
function taskCell(x: SubProject, sp: SubPlan, el?: PlanElement): string {
  const task = taskOf(x, sp, el);
  return joinTask(task.goal.lt || task.goal.en, task.acts.map((a) => a.tLt || a.t));
}

export function bookOf(c: Consolidation, plan: Record<string, unknown>, content: Obj, els: PlanElement[], inst: string): PlanBook {
  const meta = planMeta(plan, inst);
  const byId = new Map(els.map((e) => [e.id, e]));
  const codes = subCodes(c);
  const subTitle = (x: SubProject) => x.titleLt || x.title || byId.get(x.ref ?? '')?.titleLt || byId.get(x.ref ?? '')?.title || x.ref || '';
  const projects: BookProject[] = c.projects.map((p, pi) => {
    const pp = projectPlan(plan, inst, p.code);
    return {
      nr: pi + 1,
      vulsk: pp.vulskLt || pp.vulsk || vulskOf(content, p.subs.flatMap((x) => (x.ref ? [x.ref] : []))),
      title: p.titleLt || p.title,
      goal: p.purposeLt || p.purpose || '',
      parts: (pp.parts ?? []).map(({ title, goal }) => ({ title, goal })),
      lead: p.lead ?? '',
      subs: p.subs.map((x) => ({ code: codes.get(x.id)!, title: subTitle(x) })),
      state: pp.stateLt || pp.state || '',
      years: CONSOLIDATION_YEARS.map((y) => {
        const order = new Map(p.subs.map((x, i) => [x.id, i]));
        return p.milestones
          .filter((m) => m.y === y)
          .sort((a, b) => (a.sub ? (order.get(a.sub) ?? 99) : -1) - (b.sub ? (order.get(b.sub) ?? 99) : -1))
          .map((m) => m.tLt || m.t)
          .join('\n');
      }),
      resources: pp.resourcesLt || pp.resources || '',
    };
  });
  const rows: BookRow[] = c.projects.flatMap((p) => {
    const pp = projectPlan(plan, inst, p.code);
    return p.subs.map((x) => {
      const sp = subPlan(plan, inst, x.id);
      const el = byId.get(x.ref ?? '');
      // The workbook project of the project it is a task of now: one moved out of the one it came with names that one no more.
      const key = groupKeyOf(x, plan, inst);
      return {
        code: codes.get(x.id)!,
        project: (key.startsWith('part:') ? key.slice(5) : x.grp ? '' : sp.part) || pp.short || p.titleLt || p.title,
        text: taskCell(x, sp, el),
        lead: x.lead ?? '',
        kpi: sp.kpiLt || sp.kpi || x.kpiLt || x.kpi || el?.kpiLt || el?.kpi || '',
        def: sp.defLt || sp.def || '',
        base: sp.base ?? null,
        baseNote: sp.baseNoteLt || sp.baseNote || (x.ref && !sp.kpiLt ? elementText(content, x.ref, 'baseline') : ''),
        sign: sp.sign ?? '',
        unit: sp.unitLt || sp.unit || '',
        t: CONSOLIDATION_YEARS.map((_, i) => sp.t?.[i] ?? null),
        due: sp.due ?? '',
        deps: depsText(sp.condLt || sp.cond || '', sp.needs ?? [], codes),
      };
    });
  });
  return {
    names: ['Strateginis planas', 'Uždaviniai ir KPI'],
    title: meta.title || `${inst} strateginiai projektai 2026–2030`,
    notes: meta.notes ?? [],
    projects,
    blocks: meta.blocks ?? [],
    footer: meta.footer ?? [],
    kpiTitle: meta.kpiTitle || `${inst} uždaviniai ir siektini rodikliai`,
    kpiNotes: meta.kpiNotes ?? [],
    rows,
  };
}

/**
 * The institution a workbook is about, by the first words of its title ("VPC strateginiai projektai …"), when it is one
 * of `insts`: the longest whose words they are ("VU MF …" is VU MF).
 */
export function bookInstitution(book: PlanBook, insts: string[]): string {
  const words = book.title.trim().toLowerCase().split(/\s+/);
  return insts.filter((i) => !!i.trim() && i.trim().toLowerCase().split(/\s+/).every((x, k) => words[k] === x)).sort((a, b) => b.length - a.length)[0] ?? '';
}

/** The plan's entries for subprojects that are no longer there (deleted one by one): removed with them. */
export function planOrphans(plan: Record<string, unknown>, inst: string, before: Consolidation | null, after: Consolidation | null): StrategyChange[] {
  const now = new Set(after?.projects.flatMap((p) => p.subs.map((x) => x.id)) ?? []);
  return (before?.projects.flatMap((p) => p.subs.map((x) => x.id)) ?? [])
    .filter((id) => !now.has(id) && planKey(inst, id) in plan)
    .map((id) => ({ section: 'plan' as const, key: planKey(inst, id), value: null }));
}

/**
 * The plan's entries for projects that are no longer there (`<inst>|P3`): removed with them, so a project given that
 * code later does not take another's texts. A code stays its project's while it is there: deleting a project leaves the
 * others theirs. `after` null: none of them is (the consolidation removed, or proposed again: new projects from P1).
 */
export function projectPlanOrphans(plan: Record<string, unknown>, inst: string, after: Consolidation | null): StrategyChange[] {
  const now = new Set(after?.projects.map((p) => p.code) ?? []);
  return Object.keys(plan)
    .filter((key) => {
      const [i, code, more] = key.split('|');
      return i === inst && more === undefined && !!code && PROJECT_CODE.test(code) && !now.has(code);
    })
    .map((key) => ({ section: 'plan' as const, key, value: null }));
}

/** The state's plan entries, for readers that take the whole state. */
export const planOf = (state: StrategyState): Record<string, unknown> => state.plan ?? {};

// ── the views: each project's subprojects ──────────────────────────────────

/** A task as the views show it under its project (make-viewer.mjs change 14). */
export interface ViewSub {
  id: string;
  /** Its label (planLabels): its project's element, then its number (VPC-02.3). */
  code: string;
  title: { en: string; lt: string };
  goal: { en: string; lt: string };
  from: GoalFrom;
  lead: string;
  kpi: { en: string; lt: string };
  due: string;
  /** Its bar: months from January 2026. */
  s: number;
  e: number;
  /** Its bar is its own period. Not: it follows its project's bar as set in the views (make-viewer.mjs change 17). */
  own: boolean;
}

/**
 * Each project's tasks in every institution's consolidation, for the views: under the element the project is (its
 * anchor). A project that is one subproject (the element itself, or one on its own) has none: the element's row is it.
 */
export function elementSubs(content: Obj, state: StrategyState): Record<string, ViewSub[]> {
  const out: Record<string, ViewSub[]> = {};
  const plan = state.plan ?? {};
  for (const { id: inst } of institutionsOf(content, state)) {
    const c = savedConsolidation(state, inst);
    if (!c) continue;
    const byId = new Map(elementsOf(content, state, inst).map((e) => [e.id, e]));
    const labels = planLabels(c, plan, inst).sub;
    for (const p of c.projects)
      for (const g of subGroups(p, plan, inst)) {
        if (!g.anchor || g.whole) continue;
        for (const x of g.subs) {
          const el = x.ref ? byId.get(x.ref) : undefined;
          const sp = subPlan(plan, inst, x.id);
          const task = taskOf(x, sp, el);
          const per = subPeriod(x, p, state);
          (out[g.anchor] ??= []).push({
            id: x.id,
            code: labels.get(x.id) ?? '',
            title: ownText(x.title, x.titleLt, el?.title, el?.titleLt),
            goal: task.goal,
            from: task.from,
            lead: x.lead ?? '',
            kpi: sp.kpi || sp.kpiLt ? { en: sp.kpi ?? '', lt: sp.kpiLt ?? '' } : ownText(x.kpi, x.kpiLt, el?.kpi, el?.kpiLt),
            due: sp.due ?? '',
            s: per.s,
            e: per.e,
            own: per.from === 'own',
          });
        }
      }
  }
  return out;
}
